import axios from 'axios';
import { loginPath } from '../utils/authMode';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/** Проксирование внешних картинок через бэкенд (WB CDN и т.д.) */
export function proxyImg(url) {
  if (!url) return url;
  if (url.startsWith('/') || url.startsWith('data:')) return url;
  return `${API_BASE_URL}/products/image-proxy?url=${encodeURIComponent(url)}`;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
// Постоянный номер этого браузера (компьютера/ТСД). Нужен, чтобы под общей учёткой
// с разных компьютеров нельзя было сканировать в один короб (решение владельца 29.09).
let deviceId = null;
try {
  deviceId = localStorage.getItem('wms_device_id');
  if (!deviceId) {
    deviceId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('wms_device_id', deviceId);
  }
} catch { deviceId = null; }

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('wms_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (deviceId) config.headers['X-Wms-Device'] = deviceId;
  return config;
});

// ── Время: сервер отдаёт UTC «голой» строкой без таймзоны ──────────────────
// «2026-09-01T18:57:40» браузер считает СВОИМ локальным временем, поэтому история
// показывала 18:57 вместо московских 21:57. Помечаем такие строки как UTC прямо в
// ответе — тогда любой new Date()/toLocaleString в приложении считает время верно,
// без правки полутора сотен мест. Даты без времени («2026-09-01») не трогаем: это
// календарные дни, сдвигать их нельзя.
const ISO_NO_TZ = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function markUtc(value, depth = 0) {
  if (depth > 12 || value == null) return value;
  if (typeof value === 'string') {
    return ISO_NO_TZ.test(value) ? `${value.replace(' ', 'T')}Z` : value;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) value[i] = markUtc(value[i], depth + 1);
    return value;
  }
  if (typeof value === 'object') {
    for (const k of Object.keys(value)) value[k] = markUtc(value[k], depth + 1);
    return value;
  }
  return value;
}

api.interceptors.response.use((response) => {
  const ct = response.headers?.['content-type'] || '';
  if (ct.includes('application/json') && response.data) markUtc(response.data);
  return response;
});

// Авто-обновление токена по refresh_token при 401
let _isRefreshing = false;
let _refreshQueue = []; // [{resolve, reject}]

function _processQueue(error, token) {
  _refreshQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  _refreshQueue = [];
}

function _clearAuth() {
  localStorage.removeItem('wms_token');
  localStorage.removeItem('wms_refresh_token');
  localStorage.removeItem('wms_user');
}

/**
 * Короткая пауза перед повтором запроса.
 *
 * Пока сервер перезапускается после обновления, все запросы падают с 502 — на складе
 * это выглядит как «система сломалась» посреди сканирования. Молча повторяем: почти
 * всегда со второй-третьей попытки запрос проходит, и человек ничего не замечает.
 */
const _wait = (ms) => new Promise(r => setTimeout(r, ms));
const RETRY_STATUSES = [502, 503, 504];
const MAX_RETRIES = 3;

/**
 * Подтверждение выполненного действия.
 *
 * По системе только шесть страниц из семидесяти двух сообщали, что действие
 * прошло: человек нажимал, ничего не менялось на экране — и нажимал ещё раз.
 * Показываем короткое «Готово» на изменяющие запросы. Страница, которая говорит
 * об этом сама, ставит в запрос silentSuccess: true, чтобы не дублировать.
 */
let _successHandler = null;
export function setSuccessHandler(fn) { _successHandler = fn; }

/**
 * Страницы, которые сообщают об успехе сами — своей полосой или сообщением на
 * экране, а не общим уведомлением. Для них «Готово» не показываем: два сообщения
 * об одном действии сбивают с толку сильнее, чем одно.
 */
let _pagesWithOwnSuccess = [];
export function markOwnSuccessPaths(paths) { _pagesWithOwnSuccess = paths || []; }

const _WRITE_METHODS = ['post', 'put', 'patch', 'delete'];
// Служебные запросы, которые идут фоном и подтверждения не требуют
const _SILENT_PATHS = [
  '/auth/', '/scanning/', '/printers/agent', '/search', '/activity',
  '/work/summary', '/warmup', '/heartbeat', '/notify', '/stream',
];

api.interceptors.response.use(
  (response) => {
    try {
      const cfg = response.config || {};
      const method = (cfg.method || 'get').toLowerCase();
      const url = cfg.url || '';
      const page = typeof window !== 'undefined' ? window.location.pathname : '';
      const pageSaysItself = _pagesWithOwnSuccess.some(pp => page.startsWith(pp));
      if (_successHandler
          && _WRITE_METHODS.includes(method)
          && !cfg.silentSuccess
          && !pageSaysItself
          && !_SILENT_PATHS.some(p => url.includes(p))) {
        _successHandler(cfg.successMessage || 'Готово');
      }
    } catch { /* подтверждение не должно мешать самому ответу */ }
    return response;
  },
  async (error) => {
    const orig = error.config;

    // Сервер недоступен или перезапускается — повторяем сами, не тревожа человека.
    // Повторяем только чтение и идемпотентные операции: повтор POST мог бы создать
    // второй заказ или списать товар дважды.
    const method = (orig?.method || 'get').toLowerCase();
    const retriable = !error.response
      ? method === 'get'
      : RETRY_STATUSES.includes(error.response.status) && ['get', 'head', 'put'].includes(method);
    if (orig && retriable) {
      orig._retryCount = (orig._retryCount || 0) + 1;
      if (orig._retryCount <= MAX_RETRIES) {
        await _wait(600 * orig._retryCount);
        return api(orig);
      }
    }

    // Человеку — понятная причина вместо технической. Страница по-прежнему получает
    // ошибку и может показать своё сообщение, мы только дополняем текст.
    if (error.response && RETRY_STATUSES.includes(error.response.status)) {
      error.friendlyMessage = 'Система обновляется. Подождите несколько секунд и повторите.';
    } else if (!error.response) {
      error.friendlyMessage = 'Нет связи с сервером. Проверьте интернет и повторите.';
    }

    // Облако: временный пароль не сменён (сервер пускает только к профилю и смене
    // пароля). Вкладка открыта со старым профилем без флага — отмечаем и перезагружаем:
    // App.js покажет экран «Придумайте свой пароль». У Full-Fix такого ответа нет.
    const det = error.response?.data?.detail;
    if (error.response?.status === 403 && det && det.code === 'must_change_password') {
      error.friendlyMessage = det.message;
      try {
        const u = JSON.parse(localStorage.getItem('wms_user') || 'null');
        if (u && !u.must_change_password) {
          localStorage.setItem('wms_user', JSON.stringify({ ...u, must_change_password: true }));
          window.location.reload();
        }
      } catch { /* хранилище недоступно — экран смены покажет следующий /auth/me */ }
      return Promise.reject(error);
    }

    // Облако: склад за неоплату в «только просмотр» или закрыт (402, subscription.py).
    // Сообщение — как есть; закрытый склад — сразу на «Тариф и оплата» (там всё открыто)
    if (error.response?.status === 402 && error.response?.data?.code === 'payment_required') {
      error.friendlyMessage = error.response.data.detail;
      if (error.response.data.state === 'suspended' && !window.location.pathname.startsWith('/subscription')) {
        window.location.assign('/subscription');
      }
      return Promise.reject(error);
    }

    // Отказ самого входа (неверный бейдж/пароль) — это ответ формы, а не
    // истёкшая сессия: раньше промах сканом бейджа уводил терминал на /login.
    const reqUrl = orig?.url || '';
    const isAuthForm = reqUrl.includes('/auth/badge-login') || reqUrl.includes('/auth/login');
    // Публичные экраны облака (заявка, пароль по ссылке, документы) — тоже без ухода на /login
    const onLoginScreen = ['/login', '/badge', '/signup', '/set-password', '/legal/offer', '/legal/privacy']
      .includes(window.location.pathname);
    if (error.response?.status !== 401 || onLoginScreen || isAuthForm || orig._retry) {
      return Promise.reject(error);
    }

    // Сессия по бейджу — без refresh-ключа: по истечении смены на скан бейджа,
    // а не на логин и пароль, которых у сотрудника склада нет.
    const refreshToken = localStorage.getItem('wms_refresh_token');
    if (!refreshToken) {
      _clearAuth();
      window.location.href = loginPath();
      return Promise.reject(error);
    }

    if (_isRefreshing) {
      // Параллельные запросы ждут результата одного рефреша
      return new Promise((resolve, reject) => {
        _refreshQueue.push({ resolve, reject });
      }).then(token => {
        orig.headers.Authorization = `Bearer ${token}`;
        return api(orig);
      });
    }

    orig._retry = true;
    _isRefreshing = true;

    try {
      const newToken = await _refreshAcrossTabs(refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      orig.headers.Authorization = `Bearer ${newToken}`;
      _processQueue(null, newToken);
      return api(orig);
    } catch (refreshErr) {
      _processQueue(refreshErr, null);
      _clearAuth();
      window.location.href = loginPath();
      return Promise.reject(refreshErr);
    } finally {
      _isRefreshing = false;
    }
  }
);

/**
 * Обновление сессии с учётом других вкладок (аудит №302).
 *
 * Refresh-ключ одноразовый: сервер отзывает его при обмене. Две вкладки, получившие
 * 401 одновременно, шли на обмен с одним и тем же ключом — вторая получала отказ и
 * выкидывала человека на вход посреди работы. Теперь обмен идёт под общей для
 * вкладок блокировкой (Web Locks); кто зашёл вторым и видит, что ключ в хранилище
 * уже сменился, берёт готовый токен, а не меняет заново. Где блокировок нет —
 * после отказа ещё раз смотрим, не обновила ли сессию соседняя вкладка.
 */
async function _refreshAcrossTabs(seenRefresh) {
  const doRefresh = async () => {
    const current = localStorage.getItem('wms_refresh_token');
    const currentAccess = localStorage.getItem('wms_token');
    if (current && current !== seenRefresh && currentAccess) {
      return currentAccess; // соседняя вкладка уже обновила
    }
    const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: current || seenRefresh });
    const newToken = res.data.access_token;
    localStorage.setItem('wms_token', newToken);
    if (res.data.refresh_token) localStorage.setItem('wms_refresh_token', res.data.refresh_token);
    return newToken;
  };
  if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
    return navigator.locks.request('wms-auth-refresh', doRefresh);
  }
  try {
    return await doRefresh();
  } catch (err) {
    await _wait(1500);
    const current = localStorage.getItem('wms_refresh_token');
    const currentAccess = localStorage.getItem('wms_token');
    if (current && current !== seenRefresh && currentAccess) return currentAccess;
    throw err;
  }
}

// Единое правило длины пароля — как MIN_PASSWORD_LENGTH на сервере (аудит №148)
export const MIN_PASSWORD_LENGTH = 8;
export const PASSWORD_TOO_SHORT = `Пароль должен быть не менее ${MIN_PASSWORD_LENGTH} символов`;

export const authApi = {
  badgeLogin: (code) => api.post('/auth/badge-login', { code }),
  login: (username, password) => api.post('/auth/login', { username, password }),
  logout: (refreshToken) => axios.post(`${API_BASE_URL}/auth/logout`, { refresh_token: refreshToken }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  listUsers: () => api.get('/auth/users'),
  // Облако: бейдж сотрудника на учётку (выпуск гасит прежний) и наклейка PDF 58×40
  issueUserBadge: (id) => api.post(`/auth/users/${id}/badge`, null, { silentSuccess: true }),
  userBadgePdf: (id) => api.get(`/auth/users/${id}/badge.pdf`, { responseType: 'blob' }),
  createUser: (data) => api.post('/auth/users', data),
  updateUser: (id, data) => api.patch(`/auth/users/${id}`, data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`),
  changePassword: (currentPassword, newPassword) => api.post('/auth/me/change-password', { current_password: currentPassword, new_password: newPassword }),
  updateProfile: (data) => api.patch('/auth/me', data),
};

export const companiesApi = {
  getAll: () => api.get('/companies/'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies/create', data),
  update: (id, data) => api.patch(`/companies/${id}`, data),
  remove: (id, deleteUsers = false) => api.delete(`/companies/${id}`, { params: deleteUsers ? { delete_users: true } : {} }),
  getUsers: (id) => api.get(`/companies/${id}/users`),
  createUser: (id, data) => api.post(`/companies/${id}/users`, data),
  getApiToken: (id) => api.get(`/companies/${id}/api-token`),
  generateApiToken: (id) => api.post(`/companies/${id}/generate-api-token`),
  // Ключ интеграции учётки на все её компании-партнёры (12.10): в запросах X-Company-Id
  integrationKey: () => api.get('/companies/me/integration-key'),
  generateIntegrationKey: () => api.post('/companies/me/integration-key'),
  revokeIntegrationKey: () => api.delete('/companies/me/integration-key'),
  // Вебхуки клиента: адрес/секрет, проверка, журнал
  getWebhook: (id) => api.get(`/companies/${id}/webhook`),
  setWebhook: (id, data) => api.put(`/companies/${id}/webhook`, data, { silentSuccess: true }),
  testWebhook: (id) => api.post(`/companies/${id}/webhook/test`, null, { silentSuccess: true }),
  getStorageBilling: (id) => api.get(`/companies/${id}/storage-billing`),
};

export const productsApi = {
  getAll: (params) => api.get('/products/', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getGtins: (id) => api.get(`/products/${id}/gtins`),
  // Аудит №324: отчёт «GTIN у двух карточек» и снятие GTIN с карточки (склад)
  gtinDuplicates: (params) => api.get('/products/gtin-duplicates', { params }),
  removeGtin: (id, gtin, reason) => api.post(`/products/${id}/gtins/${encodeURIComponent(gtin)}/remove`, { reason: reason || null }),
  search: (article, companyId) => api.get('/products/search', { params: { article, company_id: companyId } }),
  create: (data) => api.post('/products/', data),
  wbPhoto: (query, companyId) => api.get('/products/wb-photo', { params: { query, company_id: companyId } }),
  update: (id, data) => api.put(`/products/${id}`, data),
  getMarketplaceStats: (params) => api.get('/products/stats/marketplace', { params: params || {} }),
  fbsTzStatus: (productIds) => api.get('/products/fbs-tz-status', { params: { product_ids: (productIds || []).join(',') } }),
  transferCatalog: (data) => api.post('/products/transfer-catalog', data),
  setArchived: (id, archived) => api.post(`/products/${id}/archive`, { archived }),
  bulkDelete: (ids) => api.post('/products/bulk-delete', { ids }),
  bulkSetSkipConsumables: (productIds, skip) => api.post('/products/bulk-set-skip-consumables', { product_ids: productIds, skip }),
  bulkSetFbs: (productIds, isFbs) => api.post('/products/bulk-set-fbs', { product_ids: productIds, is_fbs: isFbs }),
  bulkSetLabelAttrs: (productIds, attrs, mode, clearKeys) =>
    api.post('/products/bulk-set-label-attrs', { product_ids: productIds, attrs, mode: mode || 'fill_empty', clear_keys: clearKeys || [] }),
  getLabelAttrs: (productIds) => api.get('/products/label-attrs', { params: { ids: (productIds || []).join(',') } }),
  bulkSetFbsLimit: (productIds, limit) => api.post('/products/bulk-set-fbs-limit', { product_ids: productIds, limit }),
  getKpiSummary: (id, params) => api.get(`/products/${id}/kpi-summary`, { params: params || {} }),
  getKpiDetail: (id, params) => api.get(`/products/${id}/kpi-detail`, { params: params || {} }),
  exportKpiDetail: (id, params) => api.get(`/products/${id}/kpi-detail/export.xlsx`, { params: params || {}, responseType: 'blob' }),
  getSizeBreakdown: (id, params) => api.get(`/products/${id}/size-breakdown`, { params: params || {} }),
  exportSizeBreakdown: (id, params) => api.get(`/products/${id}/size-breakdown/export.xlsx`, { params: params || {}, responseType: 'blob' }),
  exportKpiAll: (params) => api.get('/products/export/kpi.xlsx', { params: params || {}, responseType: 'blob' }),
  // filesOrFile: File или массив File. Если массив — отправляет как files[].
  uploadMeasurementsPhoto: (id, filesOrFile) => {
    const fd = new FormData();
    const list = Array.isArray(filesOrFile) ? filesOrFile : [filesOrFile];
    if (list.length === 1) {
      fd.append('file', list[0]);
    } else {
      list.forEach(f => fd.append('files', f));
    }
    return api.post(`/products/${id}/measurements-photo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Главное фото карточки файлом (ручная загрузка) и удаление загруженного вручную
  uploadPhoto: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/products/${id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
  },
  deletePhoto: (id) => api.delete(`/products/${id}/photo`),
  // url=null — удалить все фото; url=URL — удалить только это
  deleteMeasurementsPhoto: (id, url = null) => api.delete(`/products/${id}/measurements-photo`, {
    params: url ? { url } : {},
  }),
};

// Рейсы (машины) исходящей логистики ФБС
export const trucksApi = {
  list: (params) => api.get('/trucks', { params: params || {} }),
  analytics: (params) => api.get('/trucks/analytics', { params: params || {} }),
  get: (id) => api.get(`/trucks/${id}`),
  create: (data) => api.post('/trucks', data),
  patch: (id, data) => api.patch(`/trucks/${id}`, data),
  remove: (id) => api.delete(`/trucks/${id}`),
  assign: (id, supplyIds) => api.post(`/trucks/${id}/assign`, { supply_ids: supplyIds }),
  unassign: (id, supplyIds) => api.post(`/trucks/${id}/unassign`, { supply_ids: supplyIds }),
  searchSupplies: (params) => api.get('/trucks/supplies/search', { params: params || {} }),
  bulkCost: (data) => api.post('/trucks/bulk-cost', data),
};

export const ordersApi = {
  getAll: (companyId, params = {}) => api.get('/orders/', { params: { company_id: companyId || undefined, ...params } }),
  upload: (formData) => api.post('/orders/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  previewUpload: (formData) => api.post('/orders/preview-upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (orderId) => api.delete(`/orders/${orderId}`),
  getPickingList: (orderId) => api.get(`/orders/${orderId}/picking-list`),
  updateCollected: (orderId, itemId, collected_qty) => api.patch(`/orders/${orderId}/items/${itemId}/collect`, { collected_qty }),
  searchProductsForReassort: (orderId, q) => api.get(`/orders/${orderId}/search-products`, { params: { q } }),
  createReassortment: (orderId, data) => api.post(`/orders/${orderId}/reassortment`, data),
  getReassortments: (orderId) => api.get(`/orders/${orderId}/reassortments`),
  createShipment: (orderId, data) => api.post(`/orders/${orderId}/create-shipment`, data),
};

export const templatesApi = {
  downloadOrderTemplate: () => api.get('/templates/order-template', { responseType: 'blob' }),
};

export const processingApi = {
  closeWarnings: (id) => api.get(`/processing/shipments/${id}/close-warnings`),
  // перенос короба приёмки в отгрузку целиком (скан наклейки BOX-…)
  previewArrivalBox: (shipmentId, code) =>
    api.get(`/processing/shipments/${shipmentId}/arrival-box/preview`, { params: { code } }),
  attachArrivalBox: (shipmentId, code) =>
    api.post(`/processing/shipments/${shipmentId}/arrival-box/attach`, { code }),
  getShipments: (params) => api.get('/processing/shipments', { params }),
  getShipmentsSummary: (params) => api.get('/processing/shipments/summary', { params: params || {} }),
  getShipment: (id) => api.get(`/processing/shipments/${id}`),
  startShipment: (id) => api.post(`/processing/shipments/${id}/start`),
  revertShipment: (id) => api.post(`/processing/shipments/${id}/revert`),
  completeShipment: (id, data) => api.post(`/processing/shipments/${id}/complete`, data || null),
  getDiscrepancies: (id) => api.get(`/processing/shipments/${id}/discrepancies`),
  // Расхождения «в процессе сборки» — сразу применяются к остатку
  addItemDiscrepancy: (shipmentId, itemId, data) => api.post(`/processing/shipments/${shipmentId}/items/${itemId}/discrepancies`, data),
  removeDiscrepancy: (shipmentId, discrepancyId) => api.delete(`/processing/shipments/${shipmentId}/discrepancies/${discrepancyId}`),
  updateStages: (id, configs) => api.patch(`/processing/shipments/${id}/stages`, { configs }),
  updateProgress: (progressId, actualQty) => api.patch(`/processing/progress/${progressId}`, { actual_qty: actualQty }),
  getDashboard: () => api.get('/processing/dashboard'),
  getMarketplaceStats: () => api.get('/processing/marketplace-stats'),
  getHistory: (id) => api.get(`/processing/shipments/${id}/history`),
  // options: { comment, wb_supply_formation }
  transition: (id, toStatus, optsOrComment) => {
    const opts = (typeof optsOrComment === 'string' || optsOrComment == null)
      ? { comment: optsOrComment || undefined }
      : optsOrComment;
    const params = { to_status: toStatus };
    if (opts.comment) params.comment = opts.comment;
    if (opts.wb_supply_formation != null) params.wb_supply_formation = opts.wb_supply_formation;
    // закрыть, несмотря на напечатанные и не наклеенные марки (после подтверждения)
    if (opts.confirm_orphan_kiz) params.confirm_orphan_kiz = true;
    // закрыть, хотя часть позиций не собрана (спишутся по плану) — после подтверждения
    if (opts.confirm_unscanned) params.confirm_unscanned = true;
    // закрыть, хотя маркируемый товар уезжает без кодов ЧЗ — после подтверждения (аудит №197)
    if (opts.confirm_no_kiz) params.confirm_no_kiz = true;
    // выкладка ФП: закрыть, хотя её короба не поставлены в ячейки (коробов физически нет)
    if (opts.confirm_unplaced_boxes) params.confirm_unplaced_boxes = true;
    // пустые короба перед закрытием: удалить или закрыть как есть (аудит №198)
    if (opts.drop_empty_boxes) params.drop_empty_boxes = true;
    if (opts.confirm_empty_boxes) params.confirm_empty_boxes = true;
    // отгрузка на WB без поставки: our_fact — счёт по нашему факту, later — без счёта
    if (opts.wb_billing) params.wb_billing = opts.wb_billing;
    return api.post(`/processing/shipments/${id}/transition`, null, { params });
  },
  // Cargo places (грузоместа)
  getCargoPlaces: (shipmentId) => api.get(`/processing/shipments/${shipmentId}/cargo-places`),
  createCargoPlace: (shipmentId, data) => api.post(`/processing/shipments/${shipmentId}/cargo-places`, data || {}),
  deleteCargoPlace: (cpId) => api.delete(`/processing/cargo-places/${cpId}`),
  addItemToCargoPlace: (cpId, data) => api.post(`/processing/cargo-places/${cpId}/items`, data),
  removeCargoPlaceItem: (itemId) => api.delete(`/processing/cargo-place-items/${itemId}`),
  updateCargoPlaceItem: (itemId, data) => api.patch(`/processing/cargo-place-items/${itemId}`, data),
  undoLastCargoScans: (itemId, qty) => api.post(`/processing/cargo-place-items/${itemId}/undo-last`, null, { params: { qty } }),
  autoPack: (shipmentId, maxPerBox) => api.post(`/processing/shipments/${shipmentId}/auto-pack`, null, {
    params: maxPerBox ? { max_items_per_box: maxPerBox } : {},
  }),
  printCargoLabels: (shipmentId, cargoPlaceId) => `${API_BASE_URL}/processing/shipments/${shipmentId}/cargo-places/print${cargoPlaceId ? `?cargo_place_id=${cargoPlaceId}` : ''}`,
  exportCargoExcel: (shipmentId) => `${API_BASE_URL}/processing/shipments/${shipmentId}/cargo-places/excel`,
  processingSheet: (shipmentId) => api.get(`/processing/shipments/${shipmentId}/processing-sheet`, { responseType: 'blob' }),
  // Фоновый пересчёт при открытии черновика — без «Готово» на каждый вход в карточку
  recalculateStages: (shipmentId) => api.post(`/processing/shipments/${shipmentId}/recalculate-stages`, null, { silentSuccess: true }),
  tzRecalcPreview: (shipmentId) => api.get(`/processing/shipments/${shipmentId}/tz-recalc`),
  tzRecalcApply: (shipmentId) => api.post(`/processing/shipments/${shipmentId}/tz-recalc/apply`),
  cancelShipment: (shipmentId) => api.post(`/processing/shipments/${shipmentId}/cancel`),
  updateShipment: (id, data) => api.patch(`/processing/shipments/${id}`, data),
  toFbsPrep: (id) => api.post(`/processing/shipments/${id}/to-fbs-prep`),
  chargeLanded: (id) => api.post(`/processing/shipments/${id}/charge-landed`),
  addItemToShipment: (shipmentId, data) => api.post(`/processing/shipments/${shipmentId}/items`, data),
  deleteShipmentItem: (shipmentId, itemId) => api.delete(`/processing/shipments/${shipmentId}/items/${itemId}`),
  transferShipmentItem: (shipmentId, itemId, data) => api.post(`/processing/shipments/${shipmentId}/items/${itemId}/transfer`, data),
  exportItems: (params) => api.get('/processing/shipments/export-items', { params, responseType: 'blob' }),
  exportWB: (shipmentId) => api.get(`/processing/shipments/${shipmentId}/export-wb`, { responseType: 'blob' }),
  exportServicesCalc: (shipmentId, preliminary = false) => api.get(`/processing/shipments/${shipmentId}/services-calc.xlsx`, { responseType: 'blob', params: preliminary ? { preliminary: true } : {} }),
  // Озон X-DOCK
  uploadOzonLabels: (shipmentId, file, mapping) => {
    const fd = new FormData();
    fd.append('file', file);
    if (mapping) fd.append('mapping', JSON.stringify(mapping));
    return api.post(`/processing/shipments/${shipmentId}/ozon-labels`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  ozonLabelsPdfUrl: (shipmentId) => `${API_BASE_URL}/processing/shipments/${shipmentId}/ozon-labels.pdf`,
  ozonPackageUnitsUrl: (shipmentId) => `${API_BASE_URL}/processing/shipments/${shipmentId}/ozon-package-units.xlsx`,
  deleteOzonLabels: (shipmentId) => api.delete(`/processing/shipments/${shipmentId}/ozon-labels`),
  resetMarking: (shipmentId, itemId, data) =>
    api.post(`/processing/shipments/${shipmentId}/items/${itemId}/reset-marking`, data),
  printHistory: (shipmentId) => api.get(`/processing/shipments/${shipmentId}/print-history`),
};

export const scanApi = {
  // универсальный скан: код → что это и куда идти
  resolve: (code) => api.get('/scan/resolve', { params: { code } }),
};

// Подработчики (02.10): смена, люди, рассылки, настройки бота записи
export const tempWorkersApi = {
  shift: (params) => api.get('/temp-workers/shift', { params }),
  addSignup: (data) => api.post('/temp-workers/signups', data),
  arrive: (id) => api.post(`/temp-workers/signups/${id}/arrive`),
  noShow: (id) => api.post(`/temp-workers/signups/${id}/no-show`),
  undo: (id) => api.post(`/temp-workers/signups/${id}/undo`),
  leave: (id, hours) => api.post(`/temp-workers/signups/${id}/leave`, hours == null ? {} : { hours }),
  rate: (id, data) => api.post(`/temp-workers/signups/${id}/rate`, data),
  badge: (id, print) => api.post(`/temp-workers/signups/${id}/badge`, print || null),
  people: (params) => api.get('/temp-workers/people', { params }),
  person: (id) => api.get(`/temp-workers/people/${id}`),
  updatePerson: (id, data) => api.patch(`/temp-workers/people/${id}`, data),
  message: (id, text) => api.post(`/temp-workers/people/${id}/message`, { text }),
  broadcast: (data) => api.post('/temp-workers/broadcast', data),
  settings: () => api.get('/temp-workers/settings'),
  saveSettings: (values) => api.put('/temp-workers/settings', values),
  poster: () => api.get('/temp-workers/poster.pdf', { responseType: 'blob' }),
  personOutput: (id) => api.get(`/temp-workers/people/${id}/output`),
  link: (id, employeeId) => api.post(`/temp-workers/people/${id}/link`, { employee_id: employeeId }),
};

export const warehouseActivityApi = {
  get: (params) => api.get('/warehouse-activity', { params }),
  // Сканы склада по процессам и «Кто собирал» поставку (services/scan_stats.py, 02.10)
  overview: (params) => api.get('/warehouse-activity/overview', { params }),
  shipmentScans: (shipmentId, params) => api.get(`/warehouse-activity/shipment/${shipmentId}/scans`, { params }),
};

export const arrivalsApi = {
  // Фото приёмки (брак, расхождения): список, загрузка до 10 штук, удаление
  photos: (id) => api.get(`/arrivals/${id}/photos`),
  uploadPhotos: (id, files, note, stage) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return api.post(`/arrivals/${id}/photos`, fd, {
      params: { ...(note ? { note } : {}), ...(stage ? { stage } : {}) }, headers: { 'Content-Type': 'multipart/form-data' }, silentSuccess: true, timeout: 120000,
    });
  },
  // Разгрузка машины (задачи №5–7): начать → завершить (число мест) → места «<приёмка>-N/M» → буфер → на приёмку
  unload: (id) => api.get(`/arrivals/${id}/unload`),
  printBarcodes: (id, data) => api.post(`/arrivals/${id}/print-barcodes`, data, { silentSuccess: true, timeout: 120000 }),
  acceptStart: (id) => api.post(`/arrivals/${id}/accept/start`, null, { silentSuccess: true }),
  unloadStart: (id) => api.post(`/arrivals/${id}/unload/start`, null, { silentSuccess: true }),
  unloadFinish: (id, places) => api.post(`/arrivals/${id}/unload/finish`, { places }, { silentSuccess: true }),
  unloadPlace: (id, cell_code, places) => api.post(`/arrivals/${id}/unload/place`, { cell_code, places: places || null }, { silentSuccess: true }),
  unloadTake: (id, places) => api.post(`/arrivals/${id}/unload/take`, { places: places || null }, { silentSuccess: true }),
  unloadGiveBack: (id, places) => api.post(`/arrivals/${id}/unload/give-back`, { places: places || null }, { silentSuccess: true }),
  unloadPrintLabels: (id, data) => api.post(`/arrivals/${id}/unload/print-labels`, data, { silentSuccess: true }),
  deletePhoto: (id, photoId) => api.delete(`/arrivals/${id}/photos/${photoId}`, { silentSuccess: true }),
  scanActivity: (params) => api.get('/arrivals/scan-activity', { params: params || {} }),
  setShifts: (shifts) => api.put('/arrivals/shifts', { shifts }),
  getAll: (params) => api.get('/arrivals/', { params }),
  getById: (id) => api.get(`/arrivals/${id}`),
  create: (data) => api.post('/arrivals/', data),
  upload: (file, params) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/arrivals/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, params });
  },
  downloadTemplate: () => api.get('/arrivals/template', { responseType: 'blob' }),
  previewUpload: (file, params) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/arrivals/preview-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, params });
  },
  searchProducts: (q, companyId) => api.get('/arrivals/search-products', { params: { q, ...(companyId ? { company_id: companyId } : {}) } }),
  scan: (arrivalId, barcode) => api.post(`/arrivals/${arrivalId}/scan`, null, { params: { barcode } }),
  // Приёмка по коробам: состав коробов из файла + скан ШК/ЧЗ в контексте короба
  boxesPreview: (arrivalId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/arrivals/${arrivalId}/boxes/preview-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  boxesUpload: (arrivalId, file, params) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/arrivals/${arrivalId}/boxes/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, params });
  },
  setFbsTarget: (arrivalId, val) => api.patch(`/arrivals/${arrivalId}/fbs-target`, { fbs_target: !!val }),
  // принимать маркируемый товар без скана ЧЗ (кодов нет или не читаются)
  setSkipKiz: (arrivalId, val) => api.patch(`/arrivals/${arrivalId}/skip-kiz`, { skip_kiz: !!val }),
  setMarkOnScan: (arrivalId, val, copies) => api.patch(`/arrivals/${arrivalId}/mark-on-scan`, {
    mark_on_scan: !!val,
    barcode_copies: copies?.barcode ?? null,
    kiz_copies: copies?.kiz ?? null,
  }),
  boxesList: (arrivalId) => api.get(`/arrivals/${arrivalId}/boxes`),
  boxManualAdd: (arrivalId, boxId, arrival_item_id, qty) => api.post(`/arrivals/${arrivalId}/boxes/${boxId}/manual-add`, { arrival_item_id, qty }, { silentSuccess: true }),
  boxGet: (arrivalId, boxId) => api.get(`/arrivals/${arrivalId}/boxes/${boxId}`),
  boxScan: (arrivalId, boxId, code, pendingItemId, print) => api.post(`/arrivals/${arrivalId}/boxes/${boxId}/scan`, { code, pending_item_id: pendingItemId ?? null, ...(print || {}) }),
  boxFinish: (arrivalId, boxId) => api.post(`/arrivals/${arrivalId}/boxes/${boxId}/finish`),
  boxReset: (arrivalId, boxId) => api.post(`/arrivals/${arrivalId}/boxes/${boxId}/reset`),
  // файл «Комплектация. Упаковка. Выгрузка» прямо из приёмки
  // прямая отгрузка из приёмки (минуя обработку)
  // работы, реально выполненные при приёмке — из них собирается счёт
  // сверка наших коробов с поставкой Wildberries
  wbSupplyCompare: (id, supplyId) => api.get(`/arrivals/${id}/wb-supply-compare`, { params: { supply_id: supplyId } }),
  doneServices: (id) => api.get(`/arrivals/${id}/done-services`),
  setDoneServices: (id, serviceIds) => api.put(`/arrivals/${id}/done-services`, { service_ids: serviceIds }),
  directShipPreview: (id) => api.get(`/arrivals/${id}/direct-shipment/preview`),
  directShipCreate: (id, data) => api.post(`/arrivals/${id}/direct-shipment`, data || {}),
  // Слить приёмку склада с плановой приёмкой клиента (аудит №239)
  mergeCandidates: (id) => api.get(`/arrivals/${id}/merge-candidates`),
  mergeInto: (id, plannedId) => api.post(`/arrivals/${id}/merge-into/${plannedId}`),
  packingFileCheck: (id) => api.get(`/arrivals/${id}/packing-file/check`),
  packingFile: (id) => api.get(`/arrivals/${id}/packing-file.xlsx`, { responseType: 'blob' }),
  boxesClear: (arrivalId) => api.delete(`/arrivals/${arrivalId}/boxes`),
  recoverableKiz: (arrivalId) => api.get(`/arrivals/${arrivalId}/boxes/recoverable-kiz`),
  boxPrintProblems: (arrivalId, ids) => api.get(`/arrivals/${arrivalId}/boxes/print-problems`, { params: { ids: ids.join(',') } }),
  recoverFromKiz: (arrivalId) => api.post(`/arrivals/${arrivalId}/boxes/recover-from-kiz`),
  boxesReport: (arrivalId) => api.get(`/arrivals/${arrivalId}/boxes-report`),
  boxCreate: (arrivalId) => api.post(`/arrivals/${arrivalId}/boxes/create`),
  boxScanBuild: (arrivalId, boxId, code, pendingItemId, print) => api.post(`/arrivals/${arrivalId}/boxes/${boxId}/scan-build`, { code, pending_item_id: pendingItemId ?? null, ...(print || {}) }),
  boxExport: (arrivalId, boxId) => api.get(`/arrivals/${arrivalId}/boxes/${boxId}/export.xlsx`, { responseType: 'blob' }),
  boxPrintLabel: (arrivalId, boxId, data) => api.post(`/arrivals/${arrivalId}/boxes/${boxId}/print-label`, data),
  boxItemDelete: (arrivalId, boxId, itemId) => api.delete(`/arrivals/${arrivalId}/boxes/${boxId}/items/${itemId}`),
  boxItemDecrement: (arrivalId, boxId, itemId) => api.post(`/arrivals/${arrivalId}/boxes/${boxId}/items/${itemId}/decrement`),
  boxPlace: (arrivalId, boxCode, cellCode) => api.post(`/arrivals/${arrivalId}/boxes/place`, { box_code: String(boxCode), cell_code: String(cellCode) }),
  boxPlaceBulk: (arrivalId, boxIds, cellCode) => api.post(`/arrivals/${arrivalId}/boxes/place-bulk`, { box_ids: boxIds, cell_code: String(cellCode) }, { silentSuccess: true }),
  updateItemFact: (arrivalId, itemId, factQty) => api.patch(`/arrivals/${arrivalId}/items/${itemId}`, null, { params: { fact_quantity: factQty } }),
  updateItemQty: (arrivalId, itemId, qty) => api.patch(`/arrivals/${arrivalId}/items/${itemId}`, null, { params: { quantity: qty } }),
  addItem: (arrivalId, params) => api.post(`/arrivals/${arrivalId}/items`, null, { params }),
  deleteItem: (arrivalId, itemId) => api.delete(`/arrivals/${arrivalId}/items/${itemId}`),
  conductPreview: (id, mode = 'FINAL') => api.get(`/arrivals/${id}/conduct-preview`, { params: { mode } }),
  conduct: (id, acceptanceMode = 'FINAL') => api.post(`/arrivals/${id}/conduct`, { acceptance_mode: acceptanceMode }),
  getRecount: (id) => api.get(`/arrivals/${id}/recount`),
  recount: (id, counts) => api.post(`/arrivals/${id}/recount`, { counts }),
  releaseToPlacement: (id) => api.post(`/arrivals/${id}/release-to-placement`, null, { silentSuccess: true }),
  archive: (id) => api.post(`/arrivals/${id}/archive`),
  getPickups: (params) => api.get('/arrivals/pickups', { params }),
  updatePickupStatus: (id, status, factPlaces) => api.patch(`/arrivals/pickups/${id}/status`, {
    pickup_status: status,
    ...(factPlaces !== undefined ? { pickup_fact_places: factPlaces } : {}),
  }),
  updatePickupFinance: (id, data) => api.patch(`/arrivals/pickups/${id}/finance`, data),
  getSavedLocations: (params) => api.get('/arrivals/saved-locations', { params }),
  deleteSavedLocation: (id) => api.delete(`/arrivals/saved-locations/${id}`),
  edit: (id, data) => api.patch(`/arrivals/${id}`, data),
  getSheet: (id) => api.get(`/arrivals/${id}/sheet`, { responseType: 'blob' }),
  exportKiz: (id, fmt = 'xlsx') => api.get(`/arrivals/${id}/kiz/export`, { params: { fmt }, responseType: 'blob' }),
  acceptanceAct: (id, params = {}) => api.get(`/arrivals/${id}/acceptance-act`, { params, responseType: 'blob' }),
  exportItems: (params) => api.get('/arrivals/export-items', { params, responseType: 'blob' }),
};

export const pvzReturnsApi = {
  getAll: (params) => api.get('/pvz-returns/', { params }),
  getById: (id) => api.get(`/pvz-returns/${id}`),
  create: (data) => api.post('/pvz-returns/', data),
  update: (id, data) => api.patch(`/pvz-returns/${id}`, data),
  scan: (id, payload, boxId) => api.post(`/pvz-returns/${id}/scan`, payload, { params: boxId ? { box_id: boxId } : {} }),
  changeCondition: (id, itemId, condition) =>
    api.post(`/pvz-returns/${id}/items/${itemId}/condition`, { condition }),
  deleteItem: (id, itemId) => api.delete(`/pvz-returns/${id}/items/${itemId}`),
  close: (id) => api.post(`/pvz-returns/${id}/close`),
  cancel: (id) => api.post(`/pvz-returns/${id}/cancel`),
  // Коробы возврата (Ф1): пикание годного по коробам + размещение в ячейку
  listBoxes: (id) => api.get(`/pvz-returns/${id}/boxes`),
  createBox: (id, destination) => api.post(`/pvz-returns/${id}/boxes`, null, { params: destination ? { destination } : {} }),
  deleteBox: (id, boxId) => api.delete(`/pvz-returns/${id}/boxes/${boxId}`),
  placeBox: (id, boxId, cellCode) =>
    api.post(`/pvz-returns/${id}/boxes/${boxId}/place`, null, { params: { cell_code: cellCode } }),
  unplaceBox: (id, boxId) => api.post(`/pvz-returns/${id}/boxes/${boxId}/unplace`),
  printBoxLabel: (id, boxId, data) => api.post(`/pvz-returns/${id}/boxes/${boxId}/print-label`, data),
  // Прямой URL для скачивания (с токеном через query param при необходимости)
  kizExport: (id, format = 'xlsx') =>
    api.get(`/pvz-returns/${id}/kiz-export`, { params: { format }, responseType: 'blob' }),
  returnSheet: (id) =>
    api.get(`/pvz-returns/${id}/return-sheet`, { responseType: 'blob' }),
  // Сводный экспорт за период: артикул / размер / принято (приёмки + блок ПВЗ)
  exportSummary: (params) =>
    api.get('/pvz-returns/export-summary', { params, responseType: 'blob' }),
};

// Сохранённые коммерческие расчёты (КП)
export const calculationsApi = {
  priceList: () => api.get('/calculations/price-list'),
  getAll: (params) => api.get('/calculations/', { params }),
  getById: (id) => api.get(`/calculations/${id}`),
  save: (data) => api.post('/calculations/', data),
  update: (id, data) => api.put(`/calculations/${id}`, data),
  remove: (id) => api.delete(`/calculations/${id}`),
  // Калькулятор ФБС (06.10): нормы/затраты, расчёт, факт клиента за 30 дней, КП Excel
  fbsSettings: () => api.get('/calculations/fbs/settings'),
  fbsSaveSettings: (data) => api.put('/calculations/fbs/settings', data),
  fbsCompute: (params) => api.post('/calculations/fbs/compute', { params }),
  fbsFacts: (companyId) => api.get('/calculations/fbs/facts', { params: { company_id: companyId } }),
  fbsCostKp: (params, clientName, marginPct, daysMonth) => api.post('/calculations/fbs-cost/kp.pdf', { params, client_name: clientName, margin_pct: marginPct, days_month: daysMonth }, { responseType: 'blob' }),
  fbsKp: (params, clientName) => api.post('/calculations/fbs/kp.xlsx', { params, client_name: clientName }, { responseType: 'blob' }),
  // AI-распознавание заявки со скрина (OpenRouter vision) → поля расчёта
  recognize: (files) => {
    const fd = new FormData();
    (Array.isArray(files) ? files : [files]).forEach(f => fd.append('files', f));
    return api.post('/calculations/recognize', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  recognizeKeyStatus: () => api.get('/calculations/recognize/api-key/status'),
  // OpenRouter: обновить ключ и/или прокси; проверить здоровье (мониторинг прокси)
  setOpenrouter: ({ apiKey, proxy } = {}) => api.put('/calculations/recognize/api-key', {
    ...(apiKey ? { api_key: apiKey } : {}), ...(proxy !== undefined ? { proxy } : {}),
  }),
  openrouterHealthCheck: () => api.post('/calculations/recognize/health-check'),
  setProxyPool: (proxies) => api.put('/calculations/recognize/proxies', { proxies }),
  testProxyPool: () => api.post('/calculations/recognize/proxies/test'),
  // Чат с агентом-расчётчиком: history + снимок формы + прикреплённые скрины
  agentChat: ({ messages, current, files }) => {
    const fd = new FormData();
    fd.append('messages', JSON.stringify(messages || []));
    if (current) fd.append('current', JSON.stringify(current));
    (files || []).forEach(f => fd.append('files', f));
    return api.post('/calculations/agent-chat', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

export const labelsApi = {
  generate: (data) => api.post('/labels/generate', data, { responseType: 'blob' }),
  generateDirect: (data) => api.post('/labels/generate-direct', data, { responseType: 'blob' }),
};

export const scanningApi = {
  scan: (shipmentId, barcode) => api.post('/scanning/scan', { shipment_id: shipmentId, barcode }),
  scanKiz: (shipmentId, shipmentItemId, kizCode) => api.post('/scanning/scan-kiz', { shipment_id: shipmentId, shipment_item_id: shipmentItemId, kiz_code: kizCode }),
  scanCargo: (cargoPlaceId, code) => api.post('/scanning/scan-cargo', { cargo_place_id: cargoPlaceId, code }),
  getStatus: (shipmentId) => api.get(`/scanning/status/${shipmentId}`),
  resetItem: (shipmentId, itemId) => api.post('/scanning/reset-item', null, { params: { shipment_id: shipmentId, item_id: itemId } }),
  resolveSupply: (wbSupplyId, fbsBatch, code) => api.get('/scanning/resolve-supply', { params: { wb_supply_id: wbSupplyId, fbs_batch: fbsBatch ?? undefined, code } }),
};

export const pickingApi = {
  getList: (params) => api.get('/picking/list', { params }),
  pick: (progressId, qty, itemId, cartId) => api.post('/picking/pick', { progress_id: progressId || null, item_id: itemId || null, qty, cart_id: cartId || null }),
  pickBatch: (picks, cartId) => api.post('/picking/pick-batch', { picks, cart_id: cartId || null }),
  sheetUrl: (shipmentId, token) => `${API_BASE_URL}/picking/sheet/${shipmentId}?token=${token}`,
  shipmentsSheet: (ids, pendingOnly = true) => api.get('/picking/shipments-sheet', { params: { shipment_ids: ids.join(','), pending_only: pendingOnly }, responseType: 'blob' }),
  sheetByArticlesUrl: (token, params = {}) => {
    const qs = new URLSearchParams({ token });
    if (params.article) qs.set('article', params.article);
    if (params.company_id) qs.set('company_id', params.company_id);
    return `${API_BASE_URL}/picking/sheet-by-articles?${qs.toString()}`;
  },
};

export const servicesApi = {
  getAll: (activeOnly) => api.get('/services/', { params: activeOnly !== undefined ? { active_only: activeOnly } : {} }),
  create: (data) => api.post('/services/', data),
  update: (id, data, params, cfg) => api.patch(`/services/${id}`, data, { ...(params ? { params } : {}), ...(cfg || {}) }),
  remove: (id) => api.delete(`/services/${id}`),
  priceUsage: (id) => api.get(`/services/${id}/price-usage`),
  priceHistory: (id) => api.get(`/services/${id}/price-history`),
  // Разовые услуги на отгрузке (аудит №250): в счёт при закрытии и в акт
  getShipmentManual: (shipmentId) => api.get(`/services/shipment/${shipmentId}/manual`),
  addShipmentManual: (shipmentId, data) => api.post(`/services/shipment/${shipmentId}/manual`, data),
  removeShipmentManual: (shipmentId, entryId) => api.delete(`/services/shipment/${shipmentId}/manual/${entryId}`),
  getProductServices: (productId, scope) => api.get(`/services/product/${productId}`, { params: scope ? { scope } : {} }),
  addProductService: (productId, serviceId, scope, price) => api.post(`/services/product/${productId}`, { service_id: serviceId, ...(scope ? { scope } : {}), ...(price !== undefined && price !== null ? { price } : {}) }),
  removeProductEntry: (entryId) => api.delete(`/services/product-entry/${entryId}`),
  setProductEntryPrice: (entryId, price) => api.patch(`/services/product-entry/${entryId}/price`, { price }),
  // В каких каналах у товара своё ТЗ — одним запросом вместо опроса каждого канала
  getProductTzChannels: (productId) => api.get(`/services/product/${productId}/tz-channels`),
  getTzChannels: () => api.get('/services/tz-channels'),
  cloneMainTzToChannel: (productId, scope) => api.post(`/services/product/${productId}/tz-clone`, null, { params: { scope } }),
  dropChannelTz: (productId, scope) => api.delete(`/services/product/${productId}/tz`, { params: { scope } }),
  bulkAddProductServices: (productIds, serviceIds, scope) => api.post('/services/product-bulk', { product_ids: productIds, service_ids: serviceIds, ...(scope ? { scope } : {}) }),
  // Аудит №243: отмена «Снять все» — строки ровно как были (канал + индивидуальная цена)
  restoreProductServices: (entries) => api.post('/services/product-bulk', { entries }),
  bulkRemoveProductServices: (productIds, serviceIds, scope) => api.post('/services/product-bulk-remove', { product_ids: productIds, service_ids: serviceIds || null, ...(scope ? { scope } : {}) }),
  getProductServicesCount: (productIds, scope) => api.post('/services/product-services-count', { product_ids: productIds, ...(scope ? { scope } : {}) }),
  // Наборы ТЗ (шаблоны услуг) + массовое применение
  getTemplates: () => api.get('/services/templates'),
  createTemplate: (data) => api.post('/services/templates', data),
  updateTemplate: (id, data) => api.put(`/services/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/services/templates/${id}`),
  previewApplyTemplate: (data) => api.post('/services/templates/preview', data),
  applyTemplate: (data) => api.post('/services/templates/apply', data),
  // ТЗ товаров — массовый просмотр и выгрузка в Excel
  getProductsTz: (params) => api.get('/services/products-tz', { params: params || {} }),
  getProductsTzCategories: (companyId) =>
    api.get('/services/products-tz/categories', { params: companyId ? { company_id: companyId } : {} }),
  tzImportHistory: (limit = 20) => api.get('/services/products-tz/import-history', { params: { limit } }),
  tzImportRollback: (batch, { dryRun = true } = {}) =>
    api.post('/services/products-tz/import-rollback', null, { params: { batch, dry_run: dryRun } }),
  importProductsTz: (file, { dryRun = true, companyId, scope } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/services/products-tz/import', fd, {
      params: { dry_run: dryRun, company_id: companyId || undefined, scope: scope || undefined },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
  },
  exportProductsTz: (params) => api.get('/services/products-tz/export.xlsx', {
    params: params || {},
    responseType: 'blob',
  }),
};

// Монитор активности склада (только админ)
export const activityApi = {
  monitor: (params) => api.get('/activity/monitor', { params: params || {} }),
};

export const markingApi = {
  getAll: (params) => api.get('/marking/', { params }),
  unprintKiz: (data) => api.post('/marking/unprint', data),
  getSummary: (companyIdOrParams, productId) => {
    let params;
    if (companyIdOrParams && typeof companyIdOrParams === 'object') {
      params = companyIdOrParams;
    } else {
      params = {};
      if (companyIdOrParams) params.company_id = companyIdOrParams;
      if (productId) params.product_id = productId;
    }
    return api.get('/marking/summary', { params });
  },
  bulkAdd: (data) => api.post('/marking/bulk', data),
  uploadAsync: (file, companyId, productId) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/marking/upload-async?company_id=${companyId}${productId ? `&product_id=${productId}` : ''}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 1800000, // 30 минут — passing 300+ МБ может занять много на slow uplink
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  getUploadJob: (jobId) => api.get(`/marking/upload-job/${jobId}`),
  // Массовая загрузка с проверкой
  bulkStage: (files, companyId, yandexUrl) => {
    const fd = new FormData();
    (files || []).forEach((f) => fd.append('files', f));
    let url = `/marking/bulk-stage?company_id=${companyId}`;
    if (yandexUrl) url += `&yandex_disk_url=${encodeURIComponent(yandexUrl)}`;
    return api.post(url, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 1800000,
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  getBulkStageJob: (jobId) => api.get(`/marking/bulk-stage/${jobId}`),
  getBulkReview: (jobId) => api.get(`/marking/bulk-stage/${jobId}/review`),
  getBulkPending: (companyId) => api.get('/marking/bulk-stage/pending', { params: companyId ? { company_id: companyId } : {} }),
  overrideBulkMapping: (jobId, gtin, productId, confirm = false) =>
    api.patch(`/marking/bulk-stage/${jobId}/override`, { gtin, product_id: productId, confirm }),
  commitBulk: (jobId, body) => api.post(`/marking/bulk-stage/${jobId}/commit`, body || {}),
  cancelBulk: (jobId) => api.post(`/marking/bulk-stage/${jobId}/cancel`),
  delete: (id) => api.delete(`/marking/${id}`),
  bulkDelete: (ids) => api.post('/marking/bulk-delete', { ids }),
  available: (productId) => api.get(`/marking/available/${productId}`),
  printPreview: (productId, quantity, opts = {}) => api.get('/marking/print-preview', { params: { product_id: productId, quantity, ...opts } }),
  printCodes: (data) => api.post('/marking/print', data, { responseType: 'blob' }),
  getBatches: (productId) => api.get('/marking/batches', { params: { product_id: productId } }),
  reprintCodes: (data) => api.post('/marking/reprint', data, { responseType: 'blob' }),
  availableBulk: (productIds, shipmentId) => {
    if (!productIds || productIds.length === 0) return Promise.resolve({ data: {} });
    const params = { product_ids: productIds.join(',') };
    if (shipmentId) params.shipment_id = shipmentId;
    return api.get('/marking/available-bulk', { params });
  },
  // Остатки по GTIN
  getStock: (companyId) => api.get('/marking/stock', { params: companyId ? { company_id: companyId } : {} }),
  // Коды ЧЗ по фильтру вкладки «Коды ЧЗ» — файл xlsx/txt (те же параметры, что getAll)
  exportCodes: (params, fmt) => api.get('/marking/export', { params: { ...params, fmt }, responseType: 'blob' }),
  // Коды ЧЗ на товаре в зоне ФБС (выставлен на маркетплейс): сводка и файл xlsx/txt
  onSaleCodes: (companyId) => api.get('/marking/on-sale-codes', { params: { company_id: companyId } }),
  onSaleCodesExport: (companyId, fmt) => api.get('/marking/on-sale-codes/export',
    { params: { company_id: companyId, fmt }, responseType: 'blob' }),
  // Коды ЧЗ, использованные в конкретной поставке (карточка отгрузки → «Документы»)
  shipmentCodesExport: (shipmentId, fmt) => api.get('/marking/shipment-codes/export',
    { params: { shipment_id: shipmentId, fmt }, responseType: 'blob' }),
  getIntegrityReport: (companyId) => api.get('/marking/integrity-report', { params: companyId ? { company_id: companyId } : {} }),
  // Аудит-лог
  getAudit: (params) => api.get('/marking/audit', { params }),
  getUploadHistory: (params) => api.get('/marking/upload-history', { params }),
  // Настройки
  getConfig: (companyId) => api.get(`/marking/config/${companyId}`),
  updateConfig: (companyId, data) => api.put(`/marking/config/${companyId}`, data),
};

export const stockTransferApi = {
  run: (data) => api.post('/stock-transfer', data),
  createRequest: (data) => api.post('/stock-transfer/requests', data),
  mine: () => api.get('/stock-transfer/requests/mine'),
  queue: (status = 'NEW') => api.get('/stock-transfer/requests/queue', { params: { status } }),
  approve: (id) => api.post(`/stock-transfer/requests/${id}/approve`),
  reject: (id, reason) => api.post(`/stock-transfer/requests/${id}/reject`, { reason }),
  cancel: (id) => api.post(`/stock-transfer/requests/${id}/cancel`),
};

export const warehouseApi = {
  getStocks: (params) => api.get('/warehouse/stocks', { params }),
  getStocksSplit: (params) => api.get('/warehouse/stocks-split', { params }),
  zeroSaleAlert: () => api.get('/warehouse/zero-sale-alert'),
  fbsZoneBreakdown: (productIds) => api.get('/warehouse/fbs-zone-breakdown', { params: { product_ids: productIds } }),
  // Цифры остатка по товарам — одно слово, одна формула (backend services/stock_figures)
  stockFigures: (productIds) => api.get('/warehouse/stock-figures', { params: { product_ids: productIds } }),
  reserveCheck: (productIds) => api.get('/warehouse/reserve-check', { params: { product_ids: productIds } }),
  supplyPicker: (companyId, source) => api.get('/warehouse/supply-picker', { params: { company_id: companyId, source } }),
  getSummary: (params) => {
    const p = (params && typeof params === 'object') ? params : (params ? { company_id: params } : {});
    return api.get('/warehouse/summary', { params: p });
  },
  getWarehouses: () => api.get('/warehouse/warehouses'),
  // Облако: добавить склад (название, тип) в пределах тарифа; у Full-Fix ручка отвечает 404
  createWarehouse: (data) => api.post('/warehouse/warehouses', data),
  exportStocks: (companyId, warehouseId, onlyNonzero) => api.get('/warehouse/export', { params: { ...(companyId ? { company_id: companyId } : {}), ...(warehouseId ? { warehouse_id: warehouseId } : {}), ...(onlyNonzero ? { only_nonzero: true } : {}) }, responseType: 'blob' }),
  getMovementHistory: (productIds, days = 7) => api.get('/warehouse/movement-history', { params: { product_ids: productIds.join(','), days } }),
  getStockLog: (params) => api.get('/warehouse/stock-log', { params }),
  getStockHistory: (productId, days = 365, page = 1, category = null) => api.get(`/warehouse/stock-history/${productId}`, { params: { days, page, per_page: 30, ...(category && category !== 'all' ? { category } : {}) } }),
  exportStockHistory: (productId, days = 365, category = null) => api.get(`/warehouse/stock-history/${productId}/export.xlsx`, { params: { days, ...(category && category !== 'all' ? { category } : {}) }, responseType: 'blob' }),
  getProductReserves: (productId) => api.get(`/warehouse/reserves/${productId}`),
};

export const marketplaceApi = {
  getConfigs: (companyId) => api.get('/marketplace/configs', { params: companyId ? { company_id: companyId } : {} }),
  createConfig: (data, opts) => api.post('/marketplace/configs', data, opts),
  updateConfig: (id, data, cfg) => api.patch(`/marketplace/configs/${id}`, data, cfg),
  deleteConfig: (id, opts) => api.delete(`/marketplace/configs/${id}`, opts),
  testConnection: (id) => api.post(`/marketplace/configs/${id}/test`),
  syncWB: (configId) => api.post(`/marketplace/wb/sync/${configId}`),
  syncWBProducts: (configId) => api.post(`/marketplace/wb/sync-products/${configId}`),
  // WB FBW supply workflow
  // Пропуска на склад WB: список продавца + создание. Пропуск живёт 48ч и
  // оформляется до въезда — после приёмки WB его уже не даст.
  getWBPasses: (shipmentId) => api.get(`/marketplace/wb/fbw/passes/${shipmentId}`),
  createWBPass: (shipmentId, data) => api.post(`/marketplace/wb/fbw/passes/${shipmentId}`, data),
  // Бланк пропуска: WB файла не отдаёт, PDF собирает бэкенд из данных WB
  downloadWBPass: (shipmentId, passId) =>
    api.get(`/marketplace/wb/fbw/passes/${shipmentId}/${passId}/pdf`, { responseType: 'blob' }),
  listWBFBWDraftsByShipment: (shipmentId, force = false) => api.get(`/marketplace/wb/fbw/drafts/by-shipment/${shipmentId}`, { params: force ? { force: true } : {} }),
  listWBFBWDrafts: (configId, force = false) => api.get(`/marketplace/wb/fbw/drafts/${configId}`, { params: force ? { force: true } : {} }),
  // WB FBW import as new shipment
  previewWBFBWSupply: (configId, { supply_id, preorder_id, force = false }) =>
    api.get(`/marketplace/wb/fbw/preview/${configId}`, { params: { supply_id, preorder_id, force: force || undefined } }),
  importWBFBWSupply: (configId, { supply_id, preorder_id, shipping_warehouse_id, item_overrides }) =>
    api.post(`/marketplace/wb/fbw/import/${configId}`, { supply_id, preorder_id, shipping_warehouse_id, item_overrides: item_overrides || {} }),
  // Ozon FBO/Crossdock import
  listOzonFboSupplyOrders: (configId, { force = false, type } = {}) =>
    api.get(`/marketplace/ozon/fbo/supply-orders/${configId}`, { params: { force: force || undefined, type: type || undefined } }),
  previewOzonFboSupplyOrder: (configId, supplyOrderId, force = false) =>
    api.get(`/marketplace/ozon/fbo/preview/${configId}`, { params: { supply_order_id: supplyOrderId, force: force || undefined } }),
  importOzonFboSupplyOrder: (configId, { supply_order_id, shipping_warehouse_id, item_overrides }) =>
    api.post(`/marketplace/ozon/fbo/import/${configId}`, { supply_order_id, shipping_warehouse_id, item_overrides: item_overrides || {} }),
  bindWBFBWSupply: (shipmentId, supplyId, preorderId) => api.post(`/marketplace/wb/fbw/bind/${shipmentId}`, { supply_id: supplyId, preorder_id: preorderId }),
  unbindWBFBWSupply: (shipmentId) => api.delete(`/marketplace/wb/fbw/bind/${shipmentId}`),
  refreshWBFBWStatus: (shipmentId) => api.post(`/marketplace/wb/fbw/refresh/${shipmentId}`),
  getWBFBWAcceptance: (shipmentId) => api.get(`/marketplace/wb/fbw/acceptance/${shipmentId}`),
  uploadWBFBWAcceptanceExcel: (shipmentId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/marketplace/wb/fbw/acceptance/${shipmentId}/upload-excel`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  syncOzon: (configId) => api.post(`/marketplace/ozon/sync/${configId}`),
  syncOzonProducts: (configId) => api.post(`/marketplace/ozon/sync-products/${configId}`),
  shipOzonPosting: (shipmentId) => api.post(`/marketplace/ozon/ship-posting/${shipmentId}`),
  syncYM: (configId) => api.post(`/marketplace/ym/sync/${configId}`),
  getSyncLog: (configId, limit = 20) => api.get(`/marketplace/configs/${configId}/sync-log`, { params: { limit } }),
  getSchedule: (configId) => api.get(`/marketplace/configs/${configId}/schedule`),
};

// Lamoda FBO: поставку на склад Lamoda создаём мы через API, статусы и факт приёмки читаем.
export const lamodaApi = {
  reference: () => api.get('/marketplace/lamoda/reference'),
  // Товарная этикетка 58×58: превью PNG (blob — эндпоинт под авторизацией, <img src> не подойдёт)
  labelPreview: (productId, markingCode) =>
    api.get(`/marketplace/lamoda/label-preview/${productId}`, {
      params: markingCode ? { marking_code: markingCode } : {},
      responseType: 'blob',
    }),
  labelCheck: (productId) => api.get(`/marketplace/lamoda/label-check/${productId}`),
  previewSupply: (shipmentId, body = {}) =>
    api.post(`/marketplace/lamoda/shipments/${shipmentId}/preview`, body),
  createSupply: (shipmentId, body) =>
    api.post(`/marketplace/lamoda/shipments/${shipmentId}/create`, body),
  getSupplyState: (shipmentId, { force, config_id } = {}) =>
    api.get(`/marketplace/lamoda/shipments/${shipmentId}`, {
      params: { force: force || undefined, config_id: config_id || undefined },
    }),
  cancelSupply: (shipmentId, body) =>
    api.post(`/marketplace/lamoda/shipments/${shipmentId}/cancel`, body),
  // Файл поставки для загрузки в ЛК Lamoda («Логистика» → «Поставки FBO»)
  supplyFilePreview: (shipmentId) =>
    api.get(`/marketplace/lamoda/shipments/${shipmentId}/supply-file/preview`),
  supplyFileDownload: (shipmentId, body) =>
    api.post(`/marketplace/lamoda/shipments/${shipmentId}/supply-file`, body, { responseType: 'blob' }),
};

// Персонал: профили доступа, должности BossControl, учётки сотрудников
export const staffApi = {
  sharedAccounts: () => api.get('/staff/shared-accounts'),
  disableShared: (id) => api.post(`/staff/shared-accounts/${id}/disable`),
  disableUnused: () => api.post('/staff/shared-accounts/disable-unused'),
  zones: () => api.get('/staff/zones'),
  shiftBoard: (params) => api.get('/staff/shift', { params: params || {} }),
  assignZone: (data) => api.post('/staff/shift/assign', data),
  // print — устройство и размер ленты: бейдж сразу выходит из принтера
  issueBadge: (employeeId, print) => api.post(`/staff/employees/${employeeId}/badge`, print || null),
  sections: () => api.get('/staff/sections'),
  profiles: () => api.get('/staff/profiles'),
  createProfile: (data) => api.post('/staff/profiles', data),
  updateProfile: (id, data) => api.patch(`/staff/profiles/${id}`, data),
  deleteProfile: (id) => api.delete(`/staff/profiles/${id}`),
  positions: () => api.get('/staff/positions'),
  updatePosition: (id, data) => api.patch(`/staff/positions/${id}`, data),
  employees: (params) => api.get('/staff/employees', { params: params || {} }),
  // личный профиль сотрудника (null — вернуть профиль должности)
  setEmployeeProfile: (employeeId, profileId) =>
    api.post(`/staff/employees/${employeeId}/profile`, { profile_id: profileId ?? null }),
  provision: () => api.post('/staff/provision'),
};

export const shippingWarehousesApi = {
  getAll: (activeOnly) => api.get('/shipping-warehouses/', { params: activeOnly ? { active_only: true } : {} }),
  create: (data) => api.post('/shipping-warehouses/', data),
  update: (id, data) => api.patch(`/shipping-warehouses/${id}`, data),
  remove: (id) => api.delete(`/shipping-warehouses/${id}`),
  createShipment: (data) => api.post('/shipping-warehouses/create-shipment', data),
  updatePackaging: (shipmentId, data) => api.patch(`/shipping-warehouses/shipment/${shipmentId}/packaging`, data),
  getBuckets: (id) => api.get(`/shipping-warehouses/${id}/buckets`),
  saveBuckets: (id, payload) => api.put(`/shipping-warehouses/${id}/buckets`, payload),
};

export const printersApi = {
  unconfirmedJobs: (days = 14) => api.get('/printers/jobs/unconfirmed', { params: { days } }),
  confirmJob: (id, printed) => api.post(`/printers/jobs/${id}/confirm`, { printed }),
  tsdAppInfo: () => api.get('/printers/tsd-app-info'),
  // Агент печати — только авторизованным (аудит №358): качаем запросом с токеном
  downloadAgent: () => api.get('/printers/download-agent', { responseType: 'blob' }),
  getAll: (companyId) => api.get('/printers/', { params: companyId ? { company_id: companyId } : {} }),
  create: (data) => api.post('/printers/', data),
  update: (id, data) => api.patch(`/printers/${id}`, data),
  remove: (id) => api.delete(`/printers/${id}`),
  testConnection: (id) => api.post(`/printers/${id}/test`),
  printLabels: (data) => api.post('/printers/print-labels', data),
  printProductBarcode: (data) => api.post('/printers/print-product-barcode', data),
  printMarking: (data) => api.post('/printers/print-marking', data),
  // Повторная печать одного ЧЗ по отсканированному коду (printer_id ИЛИ agent_id)
  reprintByCode: (data) => api.post('/printers/reprint-by-code', data),
  wbStickerLookup: (q) => api.get('/printers/wb-sticker-lookup', { params: { q } }),
  // Печать WB-QR заказа по отсканированному ЧЗ (товар промаркирован, QR не вышел)
  wbQrByCode: (data) => api.post('/printers/wb-qr-by-code', data),
  fbsAssemblyScan: (data) => api.post('/printers/fbs-assembly-scan', data),
  fbsAssemblyUndo: (data) => api.post('/printers/fbs-assembly-undo', data),
  getJobs: (params) => api.get('/printers/jobs', { params }),
  cancelJob: (jobId) => api.post(`/printers/jobs/${jobId}/cancel`),
  cancelAllJobs: (agentId) => api.post('/printers/jobs/cancel-all', null, { params: agentId ? { agent_id: agentId } : {} }),
  // Агенты удалённой печати
  // includeDead — вместе с мёртвыми агентами (молчат больше 14 дней); только для страницы «Принтеры»
  getAgents: (companyId, includeDead) => api.get('/printers/agents', {
    params: { ...(companyId ? { company_id: companyId } : {}), ...(includeDead ? { include_dead: true } : {}) },
  }),
  createAgent: (data) => api.post('/printers/agents', data),
  deleteAgent: (id) => api.delete(`/printers/agents/${id}`),
  queuePrint: (data) => api.post('/printers/agents/queue', data),
  queueMarking: (data) => api.post('/printers/agents/queue-marking', data),
  // Печать ФБС: QR-стикер заказа WB (ШК+ЧЗ печатаются обычными методами)
  printWbSticker: (data) => api.post('/printers/print-wb-sticker', data),
  queueWbSticker: (data) => api.post('/printers/agents/queue-wb-sticker', data),
  // Предзагрузка стикеров WB пачками (перед массовой печатью QR) — снимает 429.
  // Таймаут увеличен: до 4 пачек по 100 к WB с возможным backoff.
  prefetchWbStickers: (data) => api.post('/printers/wb-stickers/prefetch', data, { timeout: 180000 }),
  // Заказы поставки без успешного QR-задания (для «допечатать недостающие QR»)
  missingWbStickers: (data) => api.post('/printers/wb-stickers/missing', data),
  // Разделитель групп (чёрная этикетка) при батч-печати
  printSeparator: (data) => api.post('/printers/print-separator', data),
  queueSeparator: (data) => api.post('/printers/agents/queue-separator', data),
  printWbSupply: (data) => api.post('/printers/print-wb-supply', data),
  queueWbSupply: (data) => api.post('/printers/agents/queue-wb-supply', data),
  queueExtLabels: (data) => api.post('/printers/agents/queue-ext-labels', data),
};

// ── WB FBS: поставки ──
// Прослеживаемость кодов Честного Знака: где код сейчас и вся его хронология
export const kizTraceApi = {
  summary: (params) => api.get('/kiz-trace/summary', { params }),
  codes: (params) => api.get('/kiz-trace/codes', { params }),
  passport: (params) => api.get('/kiz-trace/code', { params }),
  stuck: (params) => api.get('/kiz-trace/stuck', { params }),
  print: (payload) => api.post('/kiz-trace/print', payload),
  exportUrl: (params) => {
    const q = new URLSearchParams(
      Object.entries(params || {}).filter(([, v]) => v !== '' && v != null)).toString();
    return `/api/kiz-trace/export${q ? `?${q}` : ''}`;
  },
};

export const fbsApi = {
  // не принято WB: заказы, помеченные у нас отгруженными, которых WB не получил
  unaccepted: (companyId) => api.get('/marketplace/wb/fbs/unaccepted', { params: companyId ? { company_id: companyId } : {} }),
  // проверочная партия: внутренняя «поставка» для самопроверки сканом (на WB её нет)
  checkBatchCreate: (shipmentIds, dryRun) => api.post('/marketplace/wb/fbs/check-batch', { shipment_ids: shipmentIds, dry_run: dryRun }),
  checkBatches: () => api.get('/marketplace/wb/fbs/check-batch'),
  checkBatchDetail: (batch) => api.get(`/marketplace/wb/fbs/check-batch/${batch}`),
  checkBatchScan: (batch, code, agentId) => api.post('/marketplace/wb/fbs/check-batch/scan', { batch, code, agent_id: agentId ? Number(agentId) : undefined }),
  checkBatchMark: (shipmentIds, result) => api.post('/marketplace/wb/fbs/check-batch/mark', { shipment_ids: shipmentIds, result }),
  checkBatchDissolve: (batch) => api.delete(`/marketplace/wb/fbs/check-batch/${batch}`),
  checkBatchPickingSheet: (batch, pendingOnly = true) => api.get(`/marketplace/wb/fbs/check-batch/${batch}/picking-sheet`, { params: { pending_only: pendingOnly }, responseType: 'blob' }),
  unacceptedResolve: (shipmentId, action, comment) => api.post(`/marketplace/wb/fbs/unaccepted/${shipmentId}/resolve`, { action, comment }),
  unacceptedResolveBulk: (shipmentIds, action, comment) => api.post('/marketplace/wb/fbs/unaccepted/resolve-bulk', { shipment_ids: shipmentIds, action, comment }),
  // контроль зоны ФБС: не размещено / разъезд адреса / учёт↔адреса
  zoneControl: (companyId) => api.get('/marketplace/wb/fbs/zone-control', { params: companyId ? { company_id: companyId } : {} }),
  // поиск поставки по номеру пропуска — по всем состояниям сразу
  supplySearch: (q) => api.get('/marketplace/wb/fbs/supply-search', { params: { q } }),
  // вынести несобранные заказы поставки в новую (dry_run — только превью)
  splitUnassembled: (supplyId, configId, dryRun) =>
    api.post(`/marketplace/wb/fbs/supplies/${supplyId}/split-unassembled`, null,
      { params: { config_id: configId, dry_run: !!dryRun } }),
  // вынести конкретные заказы (по номеру) из поставки в новую (dry_run — только превью)
  detachOrders: (supplyId, configId, orderNumbers, reason, dryRun) =>
    api.post(`/marketplace/wb/fbs/supplies/${supplyId}/detach-orders`,
      { order_numbers: orderNumbers, reason: reason || null },
      { params: { config_id: configId, dry_run: !!dryRun } }),
  createSupply: (data) => api.post('/marketplace/wb/fbs/supplies', data),
  deliverSupply: (supplyId, configId) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/deliver`, null, { params: { config_id: configId } }),
  splitBatch: (supplyId, configId) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/split-batch`, null, { params: { config_id: configId } }),
  startBatch: (supplyId, configId, fbsBatch) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/start-batch`, null, { params: { config_id: configId, fbs_batch: fbsBatch ?? undefined } }),
  pushSgtin: (supplyId, configId) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/push-sgtin`, null, { params: { config_id: configId } }),
  sgtinCheck: (supplyId, configId, fbsBatch) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/sgtin-check`, { params: { config_id: configId, fbs_batch: fbsBatch ?? undefined } }),
  kizAudit: (supplyId, configId, fbsBatch) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/kiz-audit`, { params: { config_id: configId, fbs_batch: fbsBatch ?? undefined } }),
  supplyBarcode: (supplyId, configId, barcodeType = 'png') => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/barcode`, { params: { config_id: configId, barcode_type: barcodeType } }),
  supplyDetail: (supplyId, configId) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}`, { params: { config_id: configId } }),
  extSupplyDoc: (supplyId, kind) => api.get(`/marketplace/wb/fbs/supplies/${encodeURIComponent(supplyId)}/ext-doc/${kind}`, { responseType: 'blob' }),
  supplyFullDetail: (supplyId, configId, fbsBatch) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/detail`, { params: { config_id: configId, fbs_batch: fbsBatch ?? undefined } }),
  setSupplyBoxes: (supplyId, configId, boxes, fbsBatch) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/boxes`, { config_id: parseInt(configId), boxes: parseInt(boxes), fbs_batch: fbsBatch ?? null }),
  manualShip: (supplyId, configId, on = true) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/manual-ship`, { on }, { params: { config_id: configId } }),
  assemblyQueue: (companyId) => api.get('/marketplace/wb/fbs/assembly-queue', { params: companyId ? { company_id: companyId } : {} }),
  mergeSupplies: ({ config_id, supply_ids, dry_run }) => api.post('/marketplace/wb/fbs/supplies/merge', { config_id, supply_ids, dry_run: !!dry_run }),
  replacementOptions: (shipmentId) => api.get(`/marketplace/wb/fbs/orders/${shipmentId}/replacement-options`),
  replaceUnit: (shipmentId, newCode, configId) =>
    api.post(`/marketplace/wb/fbs/orders/${shipmentId}/replace-unit`, { new_code: newCode, config_id: configId }),
  kizQuarantine: (companyId) => api.get('/marketplace/wb/fbs/kiz-quarantine', { params: { company_id: companyId } }),
  assemblyTask: (supplyId, configId, fbsBatch) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/assembly-task`, { params: { config_id: configId, ...(fbsBatch ? { fbs_batch: fbsBatch } : {}) } }),
  boxHint: (supplyId, configId, code) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/box-hint`, { params: { config_id: configId, code } }),
  assemblyShortage: (supplyId, configId, data) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/shortage`, data, { params: { config_id: configId } }),
  supplyKizExport: (supplyId, configId) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/kiz/export`, { params: { config_id: configId }, responseType: 'blob' }),
  returnKizToStock: (supplyId, configId, data) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/kiz/return-to-stock`, data, { params: { config_id: configId } }),
  supplyKizConfirmStuck: (supplyId, configId) => api.post(`/marketplace/wb/fbs/supplies/${supplyId}/kiz/confirm-stuck`, null, { params: { config_id: configId } }),
  pendingOrders: (companyId, opts = {}) => api.get('/marketplace/wb/fbs/pending-orders', { params: { ...(companyId ? { company_id: companyId } : {}), ...(opts.status ? { status: opts.status } : {}), ...(opts.date_from ? { date_from: opts.date_from } : {}), ...(opts.date_to ? { date_to: opts.date_to } : {}), ...(opts.search ? { search: opts.search } : {}) } }),
  dashboard: (companyId) => api.get('/marketplace/wb/fbs/dashboard', { params: companyId ? { company_id: companyId } : {} }),
  // Числа плиток Центра ФБС: parts — какие плитки открыты пользователю
  // companyId — только для «pending»/«truck» (плитки «успеем к машине» на /fbs)
  hubCounts: (parts, companyId) => api.get('/marketplace/wb/fbs/hub-counts', { params: { parts: parts.join(','), ...(companyId ? { company_id: companyId } : {}) } }),
  activity: (companyId) => api.get('/marketplace/wb/fbs/activity', { params: companyId ? { company_id: companyId } : {} }),
  shippedSummary: (companyId, opts = {}) => api.get('/marketplace/wb/fbs/shipped-summary', { params: { ...(companyId ? { company_id: companyId } : {}), date_from: opts.date_from, date_to: opts.date_to } }),
  shippedExport: (companyId, opts = {}) => api.get('/marketplace/wb/fbs/shipped-export.xlsx', { params: { ...(companyId ? { company_id: companyId } : {}), ...(opts.date_from ? { date_from: opts.date_from } : {}), ...(opts.date_to ? { date_to: opts.date_to } : {}), ...(opts.search ? { search: opts.search } : {}) }, responseType: 'blob' }),
  setTruckSchedule: (departures, buffer_minutes, orders_per_hour = 0, checkpoint_minutes = 120) => api.put('/marketplace/wb/fbs/truck-schedule', { departures, buffer_minutes, orders_per_hour, checkpoint_minutes }),
  getAutoFormMode: () => api.get('/marketplace/wb/fbs/auto-form/mode'),
  setAutoFormMode: (mode) => api.put('/marketplace/wb/fbs/auto-form/mode', { mode }),
  runAutoFormNow: () => api.post('/marketplace/wb/fbs/auto-form/run-now'),
  autoFormBatches: (opts = {}) => api.get('/marketplace/wb/fbs/auto-form/batches', { params: { ...(opts.status ? { status: opts.status } : {}), ...(opts.limit ? { limit: opts.limit } : {}) } }),
  activeSupplies: (companyId) => api.get('/marketplace/wb/fbs/active-supplies', { params: companyId ? { company_id: companyId } : {} }),
  supplyPickingSheet: (supplyId, configId, fbsBatch, pendingOnly = true) => api.get(`/marketplace/wb/fbs/supplies/${supplyId}/picking-sheet`, { params: { config_id: configId, fbs_batch: fbsBatch ?? undefined, pending_only: pendingOnly }, responseType: 'blob' }),
  priceTiers: (companyId) => api.get('/marketplace/wb/fbs/price-tiers', { params: companyId ? { company_id: companyId } : {} }),
  setPriceTiers: (companyId, tiers) => api.put('/marketplace/wb/fbs/price-tiers', { company_id: companyId || null, tiers }),
  // Исключения из сетки: группы товаров со своей ставкой сборки
  rateGroups: (companyId) => api.get('/marketplace/wb/fbs/rate-groups', { params: { company_id: companyId } }),
  createRateGroup: (data) => api.post('/marketplace/wb/fbs/rate-groups', data),
  updateRateGroup: (id, data) => api.patch(`/marketplace/wb/fbs/rate-groups/${id}`, data),
  deleteRateGroup: (id) => api.delete(`/marketplace/wb/fbs/rate-groups/${id}`),
  rateGroupProducts: (id, limit = 100) => api.get(`/marketplace/wb/fbs/rate-groups/${id}/products`, { params: { limit } }),
  rateGroupPreview: (id, q) => api.get(`/marketplace/wb/fbs/rate-groups/${id}/preview`, { params: { q } }),
  addRateGroupProducts: (id, data) => api.post(`/marketplace/wb/fbs/rate-groups/${id}/products`, data),
  removeRateGroupProduct: (id, productId) => api.delete(`/marketplace/wb/fbs/rate-groups/${id}/products`, { params: { product_id: productId } }),
  clearRateGroupProducts: (id) => api.delete(`/marketplace/wb/fbs/rate-groups/${id}/products`, { params: { all_products: true } }),
  rateGroupTiers: (id) => api.get(`/marketplace/wb/fbs/rate-groups/${id}/tiers`),
  setRateGroupTiers: (id, tiers) => api.put(`/marketplace/wb/fbs/rate-groups/${id}/tiers`, { tiers }),
  uncoveredRateProducts: (companyId, dateFrom, dateTo) => api.get('/marketplace/wb/fbs/rate-groups/uncovered', { params: { company_id: companyId, date_from: dateFrom, date_to: dateTo } }),
  wbWarehouses: (configId) => api.get('/marketplace/wb/fbs/warehouses', { params: { config_id: configId } }),
  forecast: (configId, { windowDays = 14, horizonDays = 14, withAi = false } = {}) =>
    api.get(`/marketplace/wb/fbs/forecast/${configId}`, { params: { window_days: windowDays, horizon_days: horizonDays, with_ai: withAi } }),
  publishedAmounts: (productIds, companyId) => api.get('/marketplace/wb/fbs/published-amounts', { params: { product_ids: productIds.join(','), ...(companyId ? { company_id: companyId } : {}) } }),
  shippedAmounts: (productIds, companyId) => api.get('/marketplace/wb/fbs/shipped-amounts', { params: { product_ids: productIds.join(','), ...(companyId ? { company_id: companyId } : {}) } }),
  pendingAmounts: (productIds, companyId) => api.get('/marketplace/wb/fbs/pending-amounts', { params: { product_ids: productIds.join(','), ...(companyId ? { company_id: companyId } : {}) } }),
  productWarehouseBreakdown: (productId) => api.get('/marketplace/wb/fbs/product-warehouse-breakdown', { params: { product_id: productId } }),
  setZoneLimit: (productId, zoneId, limit, warehouseId = null) => api.post('/marketplace/wb/fbs/set-zone-limit', { product_id: productId, zone_id: zoneId, warehouse_id: warehouseId, limit }),
  transferFbsStock: (productId, qty, direction) => api.post('/marketplace/wb/fbs/transfer-stock', { product_id: productId, qty, direction }),
  refreshFbsStocks: (companyId, productId = null, configId = null) => api.post('/marketplace/wb/fbs/stocks/refresh', null, { params: { company_id: companyId, product_id: productId, config_id: configId } }),
  createFbsPrep: (companyId, items, note = null) => api.post('/marketplace/wb/fbs/prep', { company_id: companyId, items, note }),
  prepPreviewFile: (file, companyId) => { const fd = new FormData(); fd.append('file', file); return api.post(`/marketplace/wb/fbs/prep/preview-file?company_id=${companyId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  prepLand: (shipmentId, items, cellCode) =>
    api.post(`/marketplace/wb/fbs/prep/${shipmentId}/land`, { items, cell_code: cellCode }),
};

export const analyticsApi = {
  getOverview: (params) => api.get('/analytics/overview', { params }),
  getBySource: (params) => api.get('/analytics/by-source', { params }),
  getByCompany: (params) => api.get('/analytics/by-company', { params }),
  getTrends: (params) => api.get('/analytics/trends', { params }),
  getTopProducts: (params) => api.get('/analytics/top-products', { params }),
  getPerformance: (params) => api.get('/analytics/performance', { params }),
};

export const notificationsApi = {
  getTelegramConfig: (companyId) => api.get('/notifications/telegram/config', { params: companyId ? { company_id: companyId } : {} }),
  saveTelegramConfig: (data, companyId) => api.post('/notifications/telegram/config', data, { params: companyId ? { company_id: companyId } : {} }),
  updateTelegramConfig: (data, companyId) => api.patch('/notifications/telegram/config', data, { params: companyId ? { company_id: companyId } : {} }),
  testTelegram: (companyId) => api.post('/notifications/telegram/test', null, { params: companyId ? { company_id: companyId } : {} }),
};

// Склад облака (Н4): Telegram сотрудника через бота платформы. В Full-Fix — 404.
export const cloudTelegramApi = {
  getLink: () => api.get('/notifications/telegram/link'),
  getStatus: () => api.get('/notifications/telegram/status'),
  unlink: () => api.delete('/notifications/telegram/link', { silentSuccess: true }),
  getSettings: () => api.get('/notifications/telegram/settings'),
  saveSettings: (groups) => api.put('/notifications/telegram/settings', { groups }, { silentSuccess: true }),
};

// Внутренний MPFit-бот: персональные уведомления сотрудникам склада
export const botApi = {
  // Привязка
  createLinkCode: () => api.post('/bot/link-code'),
  unlinkTelegram: () => api.delete('/bot/link'),
  getMyTelegram: () => api.get('/bot/me/telegram'),
  // Подписки
  getSubscriptionsMatrix: () => api.get('/bot/subscriptions/matrix'),
  updateSubscription: (data) => api.patch('/bot/subscriptions', data),
  bulkSetDefaults: () => api.post('/bot/subscriptions/bulk-defaults'),
  // Косяки
  getViolations: (params) => api.get('/bot/violations', { params }),
  ackViolation: (id) => api.post(`/bot/violations/${id}/ack`),
  // Отладка
  getStatus: () => api.get('/bot/status'),
  getLogs: (params) => api.get('/bot/logs', { params }),
  testEvent: (data) => api.post('/bot/test-event', data),
  // Per-event mutes
  getMyMutes: () => api.get('/bot/mutes'),
  setMute: (data) => api.post('/bot/mutes', data),
  unmute: (eventType) => api.delete(`/bot/mutes/${eventType}`),
};

export const productManagementApi = {
  exportProducts: (params) => api.get('/product-management/export', { params, responseType: 'blob' }),
  importProducts: (file, companyId, createMissing = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = {};
    if (companyId) params.company_id = companyId;
    if (createMissing) params.create_missing = true;
    return api.post('/product-management/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
  },
  getImportHistory: (params) => api.get('/product-management/import/history', { params }),
  downloadErrorReport: (jobId) => api.get(`/product-management/import/${jobId}/errors`, { responseType: 'blob' }),
};

// Конструктор склада: зоны с назначением, стеллажи и полки одним планом.
// apply(plan, dryRun) — предпросмотр и сохранение; сообщение об успехе экран пишет сам.
export const warehouseLayoutApi = {
  get: () => api.get('/warehouse-layout'),
  apply: (plan, dryRun) => api.post('/warehouse-layout/apply', plan,
    { params: { dry_run: !!dryRun }, silentSuccess: true }),
};

export const storageApi = {
  // Короба хранения: скан ШК короба → скан ячейки
  // Печать ШК ячеек: по одной, по зоне или диапазону стеллажей.
  // Без printer_id/agent_id бэкенд вернёт PDF (печать наклеек листами).
  printCellLabels: (payload) => api.post('/storage/cells/print-labels', payload,
    (!payload.printer_id && !payload.agent_id) ? { responseType: 'blob' } : {}),
  lookupBox: (code) => api.get('/storage/boxes/lookup', { params: { code } }),
  placeBox: (boxBarcode, cellCode) => api.post('/storage/boxes/place', { box_barcode: boxBarcode, cell_code: cellCode }),
  placementQueue: () => api.get('/storage/boxes/placement-queue'),
  // «Коробов нет — убрать из очереди»: остаток не меняется (29.09)
  dismissFromQueue: (barcode, sourceShipmentId) => api.post('/storage/boxes/placement-queue/dismiss',
    { barcode, source_shipment_id: sourceShipmentId || null }),
  placeTask: (boxBarcode, cellCode) => api.post('/storage/boxes/place-task', { box_barcode: boxBarcode, cell_code: cellCode }),
  placeIntoBox: (boxBarcode, targetBoxBarcode) => api.post('/storage/boxes/place-into-box', { box_barcode: boxBarcode, target_box_barcode: targetBoxBarcode }),
  placementHistory: (params) => api.get('/storage/placement-history', { params }),
  // Выработка сканирования (пополнение/переучёт зоны) — тот же вид, что placementStats (29.09)
  scanStats: (range, worker, companyId) => api.get('/storage/scan-stats', { params: { range, worker: worker || undefined, company_id: companyId || undefined } }),
  placementStats: (range, worker, companyId) => api.get('/storage/placement-stats', { params: { range, worker: worker || undefined, company_id: companyId || undefined } }),
  placementStatsArticles: (range, companyId, worker) => api.get('/storage/placement-stats/articles', { params: { range, company_id: companyId || undefined, worker: worker || undefined } }),
  placementHistoryCsv: (params) => api.get('/storage/placement-history', { params: { ...params, fmt: 'csv' }, responseType: 'blob' }),
  // productId — уточнение, когда ШК есть у товаров нескольких компаний (ответ kind=choose)
  whereIs: (q, productId) => api.get('/storage/where', { params: productId ? { q, product_id: productId } : { q } }),
  moveCodeToBox: (data) => api.post('/storage/where/move-to-box', data),
  printBoxLabel: (boxId, data) => api.post(`/storage/boxes/${boxId}/print-label`, data),
  purgeEmptyBoxes: (companyId) => api.post('/storage/boxes/purge-empty', null,
    companyId ? { params: { company_id: companyId } } : {}),
  productCells: (scan) => api.get('/storage/product-cells', { params: { scan } }),
  consolidateProduct: (payload) => api.post('/storage/consolidate-product', payload),
  boxRecountLookup: (code) => api.get('/storage/boxes/recount', { params: { code } }),
  boxRecountMove: ({ box_barcode, cell_code, items }) => api.post('/storage/boxes/recount-move', { box_barcode, cell_code, items }),
  // Короба физически нет: убрать из зоны, не трогая остаток товара
  disposeBox: ({ box_barcode, mode, reason }) => api.post('/storage/boxes/dispose', { box_barcode, mode, reason }),
  fbsBufferLookup: (code) => api.get('/storage/fbs-buffer/lookup', { params: { code } }),
  fbsBufferPlace: ({ product_id, actual_qty, cell_code }) => api.post('/storage/fbs-buffer/place', { product_id, actual_qty, cell_code }),
  getCells: (params) => api.get('/storage/cells', { params }),
  getSchema: () => api.get('/storage/cells/schema'),
  createCell: (data) => api.post('/storage/cells', data),
  createCellsBulk: (data, dryRun) => api.post('/storage/cells/bulk', data, { params: { dry_run: !!dryRun } }),
  deleteCell: (id) => api.delete(`/storage/cells/${id}`),
  uploadCells: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/storage/cells/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  downloadTemplate: () => api.get('/storage/cells/template', { responseType: 'blob' }),
  getZones: () => api.get('/storage/zones'),
  getPlacements: (params) => api.get('/storage/placements', { params }),
  placeProduct: (data) => api.post('/storage/placements', data),
  placeBulk: (data) => api.post('/storage/placements/bulk', data),
  removePlacement: (id) => api.delete(`/storage/placements/${id}`),
  getCellContents: (cellId) => api.get(`/storage/cells/${cellId}/contents`),
  getProductPlacements: (productId) => api.get(`/storage/products/${productId}/placements`),
  writeOffPlacements: (data) => api.post('/storage/placements/write-off', data),
  returnToClient: (data) => api.post('/storage/placements/return-to-client', data),
  movePlacements: (data) => api.post('/storage/placements/move', data),
  fbsReturnBuffer: () => api.get('/storage/fbs-return-buffer'),
  downloadPlacementsTemplate: () => api.get('/storage/placements/template', { responseType: 'blob' }),
  // Импорт адресов: клиент обязателен (артикулы ищутся только у него), dryRun — предпросмотр
  uploadPlacements: (file, { companyId, dryRun = false, withStock = false } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/storage/placements/upload', fd, {
      params: { company_id: companyId, dry_run: dryRun, with_stock: withStock },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadPlacementPhoto: (placementId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/storage/placements/${placementId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePlacementPhoto: (placementId) => api.delete(`/storage/placements/${placementId}/photo`),
};

// «Что нового» (08.10): записи об обновлениях только для своей аудитории
export const whatsNewApi = {
  feed: (minor = false) => api.get('/whats-new', { params: minor ? { minor: true } : {} }),
  summary: () => api.get('/whats-new/summary'),
  seen: (slugs) => api.post('/whats-new/seen', { slugs: slugs || null }, { silentSuccess: true }),
  adminList: () => api.get('/whats-new/admin'),
  hide: (slug) => api.post(`/whats-new/${slug}/hide`, null, { silentSuccess: true }),
  unhide: (slug) => api.post(`/whats-new/${slug}/unhide`, null, { silentSuccess: true }),
  publishNow: (slug) => api.post(`/whats-new/${slug}/publish-now`, null, { silentSuccess: true }),
};

// Квиз «Настроим кабинет» клиента фулфилмента (08.10): ответы, шаги, прогресс
export const clientOnboardingApi = {
  // silentSuccess: квиз сам показывает, что сохранено, общее «Готово» после каждого ответа только мешает
  status: (companyId) => api.get('/client-onboarding', { params: companyId ? { company_id: companyId } : {} }),
  saveProfile: (data, companyId) => api.put('/client-onboarding/profile', data, { params: companyId ? { company_id: companyId } : {}, silentSuccess: true }),
  setStep: (key, state, quizOk, companyId) => api.post(`/client-onboarding/steps/${key}`, { state, quiz_ok: quizOk }, { params: companyId ? { company_id: companyId } : {}, silentSuccess: true }),
  finish: (companyId) => api.post('/client-onboarding/finish', null, { params: companyId ? { company_id: companyId } : {}, silentSuccess: true }),
  dismiss: (companyId) => api.post('/client-onboarding/dismiss', null, { params: companyId ? { company_id: companyId } : {}, silentSuccess: true }),
  reopen: (companyId) => api.post('/client-onboarding/reopen', null, { params: companyId ? { company_id: companyId } : {}, silentSuccess: true }),
};

// Финансовый модуль собственника (owner-only). Все запросы под require_finance_access.
// «Деньги» (замена Adesk): счета, операции, переводы, справочники
export const moneyApi = {
  overview: () => api.get('/fin/overview'),
  // Автозагрузка банков (21.10): code — modulbank | tbank
  bankStatus: (code) => api.get(`/fin/bank/${code}`),
  bankConnect: (code, token) => api.put(`/fin/bank/${code}`, { token }),
  bankDisconnect: (code) => api.delete(`/fin/bank/${code}`),
  bankSync: (code, data) => api.post(`/fin/bank/${code}/sync`, data || {}),
  bankAccounts: (code) => api.get(`/fin/bank/${code}/accounts`),
  bankAddAccount: (code, number) => api.post(`/fin/bank/${code}/accounts`, { number }),
  // Этап А «Деньги 2.0» (21.10): статьи, счета и пороги, журнал
  categories: () => api.get('/fin/categories'),
  createCategory: (data) => api.post('/fin/categories', data),
  patchCategory: (id, data) => api.patch(`/fin/categories/${id}`, data),
  mergeCategory: (id, intoId) => api.post(`/fin/categories/${id}/merge`, { into_id: intoId }),
  patchAccount: (id, data) => api.patch(`/fin/accounts/${id}`, data),
  createCash: (data) => api.post('/fin/accounts/cash', data),
  audit: (entity, entityId) => api.get('/fin/audit', { params: { entity, entity_id: entityId } }),
  auditFeed: (params) => api.get('/fin/audit/feed', { params }),
  // Этап Б (21.10): календарь
  calendar: (month, accountIds) => api.get('/fin/calendar', { params: { month, ...(accountIds && accountIds.length ? { account_ids: accountIds.join(',') } : {}) } }),
  calendarDay: (day, accountIds) => api.get('/fin/calendar/day', { params: { day, ...(accountIds && accountIds.length ? { account_ids: accountIds.join(',') } : {}) } }),
  // Этап В (21.10): «самолёт» и план
  forecast: (horizon, accountIds) => api.get('/fin/forecast', { params: { horizon, ...(accountIds && accountIds.length ? { account_ids: accountIds.join(',') } : {}) } }),
  plan: (horizon) => api.get('/fin/plan', { params: { horizon } }),
  analytics: (dateFrom, dateTo) => api.get('/fin/analytics', { params: { date_from: dateFrom, date_to: dateTo } }),
  taxes: (year) => api.get('/fin/taxes', { params: { year } }),
  taxSettings: () => api.get('/fin/taxes/settings'),
  saveTaxSettings: (data) => api.put('/fin/taxes/settings', data),
  statementPreview: (file, mapping) => { const fd = new FormData(); fd.append('file', file); if (mapping) fd.append('mapping', JSON.stringify(mapping)); return api.post('/fin/statement/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  statementApply: (file, mapping) => { const fd = new FormData(); fd.append('file', file); if (mapping) fd.append('mapping', JSON.stringify(mapping)); return api.post('/fin/statement/apply', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  rules: () => api.get('/fin/rules'),
  createRule: (data) => api.post('/fin/rules', data),
  updateRule: (id, data) => api.put(`/fin/rules/${id}`, data),
  applyRules: (dryRun) => api.post('/fin/rules/apply', null, { params: { dry_run: dryRun } }),
  splitTx: (id, parts) => api.post(`/fin/transactions/${id}/split`, { parts }),
  categoryGroup: (data) => api.post('/fin/categories/groups', data),
  envelopes: () => api.get('/fin/envelopes'),
  envelopesSuggest: () => api.get('/fin/envelopes/suggest'),
  saveEnvelopes: (data) => api.put('/fin/envelopes', data),
  envelopeMove: (data) => api.post('/fin/envelopes/move', data),
  envelopeMoves: () => api.get('/fin/envelopes/moves'),
  cancelEnvelopeMove: (id) => api.post(`/fin/envelopes/moves/${id}/cancel`),
  txEnvelope: (id, envelopeId) => api.put(`/fin/transactions/${id}/envelope`, { envelope_id: envelopeId || null }),
  patch: (id, data) => api.patch(`/fin/transactions/${id}`, data),
  txReceipts: (id) => api.get(`/fin/transactions/${id}/receipts`),
  accounts: () => api.get('/fin/accounts'),
  dictionaries: () => api.get('/fin/dictionaries'),
  transactions: (params) => api.get('/fin/transactions', { params }),
  create: (data) => api.post('/fin/transactions', data),
  transfer: (data) => api.post('/fin/transfers', data),
  reverse: (id, reason) => api.post(`/fin/transactions/${id}/reverse`, { reason }),
  cashflow: (params) => api.get('/fin/reports/cashflow', { params }),
  pnl: (params) => api.get('/fin/reports/pnl', { params }),
  contractors: (params) => api.get('/fin/reports/contractors', { params }),
  clientsSummary: (params) => api.get('/fin/clients/summary', { params }),
  clientsReceipts: (params) => api.get('/fin/clients/receipts', { params }),
  clientsMatching: () => api.get('/fin/clients/matching'),
  clientsMatchingApply: () => api.post('/fin/clients/matching'),
  clientsLink: (txId, topupId) => api.post(`/fin/clients/receipts/${txId}/link`, { topup_request_id: topupId }),
  clientsCredit: (txId, companyId) => api.post(`/fin/clients/receipts/${txId}/credit`, { company_id: companyId }),
  botSettings: () => api.get('/fin/bot-settings'),
  saveBotSettings: (data) => api.put('/fin/bot-settings', data),
  employeeBalances: (onlyUnpaid = true) => api.get('/fin/employees/balances', { params: { only_unpaid: onlyUnpaid } }),
  transferCandidates: (txId) => api.get(`/fin/transactions/${txId}/transfer-candidates`),
  mergeTransfer: (txIds) => api.post('/fin/transfers/merge', { tx_ids: txIds }, { silentSuccess: true }),
  unmergeTransfer: (txId) => api.post(`/fin/transfers/${txId}/unmerge`, null, { silentSuccess: true }),
  employeePeriods: (employeeId) => api.get(`/fin/employees/${employeeId}/periods`),
  setEmployeePenalty: (paymentId, penalty, comment) => api.post(`/fin/employees/periods/${paymentId}/penalty`, { penalty, comment: comment || null }, { silentSuccess: true }),
};

export const ownerFinanceApi = {
  // Дашборд недели
  getWeek: (date) => api.get('/finance/dashboard/week', { params: { date } }),
  getPnl: () => api.get('/finance/pnl'),
  getTodayLive: () => api.get('/finance/today-live'),
  getDayFact: (date) => api.get('/finance/day-fact', { params: { date } }),
  getDayFactDetails: (date, zone) => api.get('/finance/day-fact/details', { params: { date, zone } }),
  setConsumableCost: (data) => api.put('/finance/consumable-cost', data, { silentSuccess: true }),
  getTodayLiveCalibration: (days) => api.get('/finance/today-live/calibration', { params: { days } }),
  getPnlDetail: (params) => api.get('/finance/pnl/detail', { params }),
  getPnlTransactions: (params) => api.get('/finance/pnl/transactions', { params }),
  exportPnl: (params) => api.get('/finance/pnl/export.csv', { params, responseType: 'blob' }),
  getCashflow: (params) => api.get('/finance/cashflow', { params }),
  // Сведение периода (пересчёт по зонам против списанного)
  getPeriodRecalc: (params) => api.get('/finance/period-recalc', { params }),
  exportPeriodRecalc: (params) => api.get('/finance/period-recalc/export.csv', { params, responseType: 'blob' }),
  // Закрытие расчётного месяца
  closeBillingPeriod: (key, data) => api.post(`/finance/periods/${key}/close`, data || {}),
  reopenBillingPeriod: (key) => api.post(`/finance/periods/${key}/reopen`),
  // Плановые платежи
  getPlannedPayments: (params) => api.get('/finance/planned-payments', { params }),
  createPlannedPayment: (data) => api.post('/finance/planned-payments', data),
  updatePlannedPayment: (id, data) => api.put(`/finance/planned-payments/${id}`, data),
  markPlannedPaid: (ids, reason) => api.post('/finance/planned-payments/mark-paid', { ids, reason: reason || null }),
  payPlannedPayment: (id, data) => api.post(`/finance/planned-payments/${id}/pay`, data),
  // Отмена ошибочной оплаты (последней) с откатом выплаты ЗП — аудит №273
  cancelPlannedPayment: (id, data) => api.post(`/finance/planned-payments/${id}/cancel-payment`, data || {}),
  payWeekly: (data) => api.post('/finance/planned-payments/pay-weekly', data),
  deletePlannedPayment: (id, force) => api.delete(`/finance/planned-payments/${id}`, force ? { params: { force: true } } : undefined),
  exportPlannedPayments: (params) => api.get('/finance/planned-payments/export.csv', { params, responseType: 'blob' }),
  // Долги
  getDebts: (params) => api.get('/finance/debts', { params }),
  createDebt: (data) => api.post('/finance/debts', data),
  updateDebt: (id, data) => api.put(`/finance/debts/${id}`, data),
  payDebt: (id, data) => api.post(`/finance/debts/${id}/payments`, data),
  debtPayments: (id) => api.get(`/finance/debts/${id}/payments`),
  cancelDebtPayment: (id, paymentId, reason) => api.post(`/finance/debts/${id}/payments/${paymentId}/cancel`, { reason: reason || null }, { silentSuccess: true }),
  exportDebts: () => api.get('/finance/debts/export.csv', { responseType: 'blob' }),
  // Зарплаты
  updateEmployee: (id, data) => api.patch(`/finance/employees/${id}`, data),
  exportWeeklySummary: (date) => api.get('/finance/salary/weekly-summary/export.csv', { params: { date }, responseType: 'blob' }),
  getWeekTimesheet: (date) => api.get('/finance/salary/week-timesheet', { params: { date } }),
  saveTimesheetCell: (data) => api.put('/finance/salary/timesheet-cell', data),
  weekAddToPlanned: (data) => api.post('/finance/salary/week/add-to-planned', data),
  weekUndoPlanned: (data) => api.post('/finance/salary/week/undo', data),
  // BossControl
  syncBossControl: (params) => api.post('/finance/sync/boss-control', null, { params }),
};

export const labelTemplatesApi = {
  getAll: () => api.get('/label-templates/'),
  create: (data) => api.post('/label-templates/', data),
  update: (id, data) => api.patch(`/label-templates/${id}`, data),
  remove: (id) => api.delete(`/label-templates/${id}`),
  copy: (data) => api.post('/label-templates/copy', data),
  setDefault: (id) => api.post(`/label-templates/set-default/${id}`),
};

export const knowledgeApi = {
  // Публичные
  getArticles: (params) => api.get('/kb/articles', { params }),
  getArticle: (slug) => api.get(`/kb/articles/${slug}`),
  getCategories: () => api.get('/kb/categories'),
  search: (q, params) => api.get('/kb/search', { params: { q, ...params } }),
  sendFeedback: (articleId, data) => api.post(`/kb/articles/${articleId}/feedback`, data),
  // Админские
  createArticle: (data) => api.post('/kb/articles', data),
  updateArticle: (id, data) => api.put(`/kb/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/kb/articles/${id}`),
  getVersions: (id) => api.get(`/kb/articles/${id}/versions`),
  revertVersion: (id, versionId) => api.post(`/kb/articles/${id}/revert/${versionId}`),
  getAnalytics: () => api.get('/kb/analytics'),
};

export const defectsApi = {
  getSummary: (params) => api.get('/defects/summary', { params }),
  addArrivalDiscrepancies: (arrivalId, items) => api.post(`/arrivals/${arrivalId}/discrepancies`, items),
  // Брак клиентов в ячейках брака (аудит №137/№293)
  clientBrak: (params) => api.get('/storage/client-brak', { params }),
  // Выдать брак клиенту / утилизировать из ячейки брака (аудит №137)
  clientBrakIssue: (data) => api.post('/storage/client-brak/issue', data),
};

export const billingApi = {
  tzDrift: (days = 60) => api.get('/billing/tz-drift', { params: { days }, timeout: 180000 }),
  wbFboUnbilled: (olderThanDays = 0) => api.get('/billing/wb-fbo-unbilled', { params: { older_than_days: olderThanDays } }),
  chargeWbByOurFact: (shipmentId) => api.post(`/billing/shipments/${shipmentId}/charge-wb-by-our-fact`),
  getBalance: (companyId) => api.get(`/billing/companies/${companyId}/balance`),
  // Балансы всех компаний одним запросом — только склад (ADMIN / право «finance»)
  listBalances: () => api.get('/billing/balances'),
  getTransactions: (companyId, params) => api.get(`/billing/companies/${companyId}/transactions`, { params }),
  // Операции одной строкой на документ (отгрузку/приёмку) — экран «Финансы»
  getTransactionsGrouped: (companyId, params) => api.get(`/billing/companies/${companyId}/transactions-grouped`, { params }),
  // Итог за период по видам — тем же расчётом, что акт
  getPeriodSummary: (companyId, params) => api.get(`/billing/companies/${companyId}/period-summary`, { params, timeout: 120000 }),
  getBreakdown: (id) => api.get(`/billing/transactions/${id}/breakdown`),
  // Phase 3
  getSettings: (companyId) => api.get(`/billing/companies/${companyId}/settings`),
  adminTopup: (companyId, data) => api.post(`/billing/companies/${companyId}/topup`, data),
  // Ручной ввод оплат клиентов и журнал поступлений (аудит №267)
  addPayment: (companyId, data) => api.post(`/billing/companies/${companyId}/payments`, data),
  listPayments: (params) => api.get('/billing/payments', { params }),
  // Списание долга складом — отдельный вид, не оплата (аудит №128)
  debtWriteoff: (companyId, data) => api.post(`/billing/companies/${companyId}/debt-writeoff`, data),
  manualCharge: (companyId, data) => api.post(`/billing/companies/${companyId}/manual-charge`, data),
  markAsDebtWriteoff: (txId, data) => api.post(`/billing/transactions/${txId}/as-debt-writeoff`, data || {}),
  // Отчёт «Отгружено без ТЗ / ТЗ только приёмка» (аудит №125/№327)
  reportShippedWithoutTz: (params) => api.get('/billing/reports/shipped-without-tz', { params, timeout: 120000 }),
  // Перепечатки и перерасход марок/этикеток — только отчёт (аудит №359)
  reportPrintOveruse: (params) => api.get('/billing/reports/print-overuse', { params, timeout: 120000 }),
  createTopupRequest: (companyId, data) => api.post(`/billing/companies/${companyId}/topup-requests`, data),
  listTopupRequests: (params) => api.get('/billing/topup-requests', { params }),
  approveTopup: (id) => api.post(`/billing/topup-requests/${id}/approve`),
  rejectTopup: (id, data) => api.post(`/billing/topup-requests/${id}/reject`, data),
  setOverdraft: (companyId, data) => api.patch(`/billing/companies/${companyId}/overdraft`, data),
  setStoragePricing: (companyId, data) => api.patch(`/billing/companies/${companyId}/storage-pricing`, data),
  setFlags: (companyId, data) => api.patch(`/billing/companies/${companyId}/flags`, data),
  getServicePrices: (companyId) => api.get(`/billing/companies/${companyId}/prices`),
  upsertServicePrices: (companyId, items) => api.put(`/billing/companies/${companyId}/prices`, items),
  getShippingPrices: (companyId) => api.get(`/billing/companies/${companyId}/shipping-prices`),
  upsertShippingPrices: (companyId, items) => api.put(`/billing/companies/${companyId}/shipping-prices`, items),
  recalcPackaging: (data, apply) => api.post('/billing/recalc-packaging', data, { params: { apply } }),
  importInitial: (formData) => api.post('/billing/import-initial', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  recalcBalance: (companyId) => api.post(`/billing/companies/${companyId}/recalc-balance`),
  // Скачивание акта (возвращает blob)
  downloadStatementXlsx: (companyId, params) =>
    api.get(`/billing/companies/${companyId}/statement.xlsx`, { params, responseType: 'blob' }),
  // Сводный акт за период — «услуга, количество, цена, сумма» по зонам
  getActExcel: (companyId, params) =>
    api.get(`/billing/companies/${companyId}/act.xlsx`, { params, responseType: 'blob' }),
  downloadStatementPdf: (companyId, params) =>
    api.get(`/billing/companies/${companyId}/statement.pdf`, { params, responseType: 'blob' }),
  // Phase 4 — cron triggers
  runStorageBilling: (data) => api.post('/billing/storage/run-now', data || {}),
  listStorageRuns: () => api.get('/billing/storage/runs'),
  previewStorageBilling: (periodKey) => api.get('/billing/storage/preview', { params: { period_key: periodKey } }),
  syncAdesk: (data) => api.post('/billing/sync-adesk', data || {}),
};

export const consumablesApi = {
  // Справочник
  list: (params) => api.get('/consumables/', { params }),
  create: (data) => api.post('/consumables/', data),
  update: (id, data) => api.put(`/consumables/${id}`, data),
  remove: (id) => api.delete(`/consumables/${id}`),
  // Остатки и движения
  getLowStockAlerts: () => api.get('/consumables/low-stock-alerts'),
  // Инвентаризация: поставить факт нашего и/или клиентского остатка (07.10)
  inventory: (id, data) => api.post(`/consumables/${id}/inventory`, data),
  // Привязки product_consumables
  getProductLinks: (productId) => api.get(`/consumables/_/products/${productId}`),
  upsertProductLinks: (productId, items) => api.put(`/consumables/_/products/${productId}`, items),
  bulkBindProductsConsumable: (data) => api.post('/consumables/_/products/bulk-bind', data),
  getProductsBoundSummary: (productIds) => api.post('/consumables/_/products/bound-summary', { product_ids: productIds }),
  // Приёмки
  listArrivals: (params) => api.get('/consumables/_/arrivals', { params }),
  createArrival: (data) => api.post('/consumables/_/arrivals', data),
  completeArrival: (id) => api.post(`/consumables/_/arrivals/${id}/complete`),
  // По отгрузке
  previewShipment: (shipmentId) => api.get(`/consumables/_/shipments/${shipmentId}/preview`),
  // Позиции без пакета и выбор пакета сотрудником склада из карточки отгрузки
  getPackageGaps: (shipmentId) => api.get(`/consumables/_/shipments/${shipmentId}/package-gaps`),
  chooseItemPackage: (shipmentId, itemId, consumableId) =>
    api.put(`/consumables/_/shipments/${shipmentId}/items/${itemId}/package`, { consumable_id: consumableId }),
  // Per-item / bulk override (наши/клиентские)
  setShipmentManualBulk: (shipmentId, bulk) => api.put(`/consumables/_/shipments/${shipmentId}/manual`, { bulk }),
  // Остатки расходников текущего клиента (вкладка «Расходники» в кабинете)
  myStock: () => api.get('/consumables/_/my-stock'),
};

export const calculatorApi = {
  // Тарифы коробов WB — читаются из суточного снапшота в БД (к WB не ходят).
  getWbBoxTariffs: (params) => api.get('/calculator/wb/box-tariffs', { params }),
  // Принудительное обновление снапшота из WB (только admin, редко — rate-limit WB).
  refreshWbBoxTariffs: (params) => api.post('/calculator/wb/box-tariffs/refresh', null, { params }),
  // Тарифы возврата продавцу (обратная логистика) — тоже суточный снапшот.
  getWbReturnTariffs: (params) => api.get('/calculator/wb/return-tariffs', { params }),
  refreshWbReturnTariffs: (params) => api.post('/calculator/wb/return-tariffs/refresh', null, { params }),
};

// Задачи склада (27.10): поручения сотрудникам и задачи клиентов складу
export const tasksApi = {
  meta: () => api.get('/tasks/meta'),
  counts: () => api.get('/tasks/counts'),
  companies: () => api.get('/tasks/companies'),
  staff: () => api.get('/tasks/staff'),
  list: (params) => api.get('/tasks', { params }),
  get: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.patch(`/tasks/${id}`, data),
  take: (id) => api.post(`/tasks/${id}/take`),
  assign: (id, data) => api.post(`/tasks/${id}/assign`, data),
  setStatus: (id, status) => api.post(`/tasks/${id}/status`, { status }),
  done: (id, data) => api.post(`/tasks/${id}/done`, data || {}),
  cancel: (id, reason) => api.post(`/tasks/${id}/cancel`, { reason: reason || null }),
  reopen: (id) => api.post(`/tasks/${id}/reopen`),
  remove: (id) => api.delete(`/tasks/${id}`),
  // 28.10 (перенос из BZNS-Dash): канбан, проверка, проблема, подзадачи, шаблоны, аналитика
  move: (id, status, position, columnId) => api.post(`/tasks/${id}/move`, { status, position, column_id: columnId || null }),
  boards: (all) => api.get('/tasks/boards', { params: all ? { all: true } : {} }),
  createBoard: (data) => api.post('/tasks/boards', data),
  updateBoard: (id, data) => api.patch(`/tasks/boards/${id}`, data),
  review: (id) => api.post(`/tasks/${id}/review`),
  returnToWork: (id, comment) => api.post(`/tasks/${id}/return`, { comment: comment || null }),
  problem: (id, on, note) => api.post(`/tasks/${id}/problem`, { on, note: note || null }),
  addSubtask: (id, data) => api.post(`/tasks/${id}/subtasks`, data),
  updateSubtask: (id, sid, data) => api.patch(`/tasks/${id}/subtasks/${sid}`, data),
  deleteSubtask: (id, sid) => api.delete(`/tasks/${id}/subtasks/${sid}`),
  templates: () => api.get('/tasks/templates'),
  createTemplate: (data) => api.post('/tasks/templates', data),
  updateTemplate: (id, data) => api.patch(`/tasks/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/tasks/templates/${id}`),
  runTemplate: (id) => api.post(`/tasks/templates/${id}/run`),
  analytics: (days) => api.get('/tasks/analytics', { params: { days } }),
  ping: () => api.get('/tasks/ping'),
  search: (q) => api.get('/tasks/search', { params: { q } }),
  addDependency: (id, blockerId) => api.post(`/tasks/${id}/dependencies`, { blocker_id: blockerId }),
  removeDependency: (id, blockerId) => api.delete(`/tasks/${id}/dependencies/${blockerId}`),
  ai: (id, question, history) => api.post(`/tasks/${id}/ai`, { question, history }),
  exportXlsx: (params) => api.get('/tasks/export.xlsx', { params, responseType: 'blob' }),
  importFile: (file, apply, boardId) => {
    const fd = new FormData();
    fd.append('file', file); fd.append('apply', apply ? 'true' : 'false');
    if (boardId) fd.append('board_id', String(boardId));
    return api.post('/tasks/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  autoSettings: () => api.get('/tasks/auto'),
  autoToggle: (enabled) => api.post('/tasks/auto', { enabled }),
  notifications: () => api.get('/tasks/notifications'),
  readNotifications: (ids) => api.post('/tasks/notifications/read', { ids: ids || null }, { silentSuccess: true }),
  comment: (id, text, file) => {
    const fd = new FormData();
    if (text) fd.append('text', text);
    if (file) fd.append('file', file);
    return api.post(`/tasks/${id}/comments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  fileBlob: (id, commentId) => api.get(`/tasks/${id}/files/${commentId}`, { responseType: 'blob' }),
  types: (all) => api.get('/tasks/types', { params: all ? { all: true } : {} }),
  createType: (data) => api.post('/tasks/types', data),
  updateType: (id, data) => api.patch(`/tasks/types/${id}`, data),
};

// CRM full-fix (внутренний sales-модуль)
export const crmApi = {
  me: () => api.get('/crm/me'),
  dashboard: () => api.get('/crm/dashboard'),
  myDay: () => api.get('/crm/my-day'),
  overview: () => api.get('/crm/overview'),
  pipelines: () => api.get('/crm/pipelines'),
  sources: () => api.get('/crm/sources'),
  managers: () => api.get('/crm/managers'),
  leadStatuses: () => api.get('/crm/lead-statuses'),
  // Лиды
  listLeads: (params) => api.get('/crm/leads', { params }),
  getLead: (id) => api.get(`/crm/leads/${id}`),
  createLead: (data) => api.post('/crm/leads', data),
  updateLead: (id, data) => api.patch(`/crm/leads/${id}`, data),
  setLeadStatus: (id, statusId, comment, force) => api.post(`/crm/leads/${id}/status`, { status_id: statusId, comment, force: !!force }),
  resumeLead: (id) => api.post(`/crm/leads/${id}/resume`),
  assignLead: (id, userId) => api.post(`/crm/leads/${id}/assign`, { user_id: userId }),
  markLead: (id, status) => api.post(`/crm/leads/${id}/mark`, null, { params: { status } }),
  convertLead: (id, data) => api.post(`/crm/leads/${id}/convert`, data),
  deleteLead: (id) => api.delete(`/crm/leads/${id}`),
  contactDuplicates: (params) => api.get('/crm/contacts/duplicates', { params }),
  // Сделки
  listDeals: (params) => api.get('/crm/deals', { params }),
  createDeal: (data) => api.post('/crm/deals', data),
  getDeal: (id) => api.get(`/crm/deals/${id}`),
  updateDeal: (id, data) => api.patch(`/crm/deals/${id}`, data),
  moveDeal: (id, stageId, extra) => api.post(`/crm/deals/${id}/move`, { stage_id: stageId, ...(extra || {}) }),
  setDealStatus: (id, status, lostReason) => api.post(`/crm/deals/${id}/status`, { status, lost_reason: lostReason }),
  resumeDeal: (id) => api.post(`/crm/deals/${id}/resume`),
  deleteDeal: (id, reason) => api.delete(`/crm/deals/${id}`, { params: reason ? { reason } : {} }),
  addDealContact: (id, contactId, role) => api.post(`/crm/deals/${id}/contacts`, { contact_id: contactId, role }),
  removeDealContact: (id, contactId) => api.delete(`/crm/deals/${id}/contacts/${contactId}`),
  dealChecklist: (id) => api.get(`/crm/deals/${id}/checklist`),
  markChecklist: (id, itemId, data) => api.put(`/crm/deals/${id}/checklist/${itemId}`, data),
  // Контакты
  listContacts: (params) => api.get('/crm/contacts', { params }),
  contactsStats: () => api.get('/crm/contacts/stats'),
  getContact: (id) => api.get(`/crm/contacts/${id}`),
  createContact: (data) => api.post('/crm/contacts', data),
  updateContact: (id, data) => api.patch(`/crm/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/crm/contacts/${id}`),
  exportContactsUrl: (params) => `/api/crm/contacts/export.xlsx${params ? '?' + new URLSearchParams(params).toString() : ''}`,
  contactsImportTemplate: () => api.get('/crm/contacts/import-template.xlsx', { responseType: 'blob' }),
  exportContacts: (params) => api.get('/crm/contacts/export.xlsx', { params, responseType: 'blob' }),
  contactsImportPreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/crm/contacts/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  contactsImportApply: (rows, onDuplicate) => api.post('/crm/contacts/import/apply', { rows, on_duplicate: onDuplicate || 'update' }),
  // Организации
  listOrganizations: (params) => api.get('/crm/organizations', { params }),
  getOrganization: (id) => api.get(`/crm/organizations/${id}`),
  createOrganization: (data) => api.post('/crm/organizations', data),
  updateOrganization: (id, data) => api.patch(`/crm/organizations/${id}`, data),
  deleteOrganization: (id) => api.delete(`/crm/organizations/${id}`),
  organizationDeals: (id) => api.get(`/crm/organizations/${id}/deals`),
  organizationWms: (id) => api.get(`/crm/organizations/${id}/wms`),
  organizationTerms: (id) => api.get(`/crm/organizations/${id}/terms`),
  saveOrganizationTerms: (id, terms, termsNote) => api.put(`/crm/organizations/${id}/terms`, { terms, terms_note: termsNote }),
  applyTermsToWms: (id, dryRun) => api.post(`/crm/organizations/${id}/terms/apply-wms`, null, { params: { dry_run: dryRun !== false } }),
  termsByCompany: (companyId) => api.get(`/crm/terms/by-company/${companyId}`),
  // Задачи
  listActivities: (params) => api.get('/crm/activities', { params }),
  createActivity: (data) => api.post('/crm/activities', data),
  updateActivity: (id, data) => api.patch(`/crm/activities/${id}`, data),
  completeActivity: (id, result) => api.post(`/crm/activities/${id}/done`, result ? { result } : {}),
  cancelActivity: (id, result) => api.post(`/crm/activities/${id}/cancel`, result ? { result } : {}),
  reopenActivity: (id) => api.post(`/crm/activities/${id}/reopen`),
  deleteActivity: (id) => api.delete(`/crm/activities/${id}`),
  // Комментарии
  listComments: (entity, entityId) => api.get('/crm/comments', { params: { entity, entity_id: entityId } }),
  createComment: (entity, entityId, text, mentions) => api.post('/crm/comments', { entity, entity_id: entityId, text, mentions }),
  updateComment: (id, data) => api.patch(`/crm/comments/${id}`, data),
  deleteComment: (id) => api.delete(`/crm/comments/${id}`),
  // Файлы
  listFiles: (entity, entityId) => api.get('/crm/files', { params: { entity, entity_id: entityId } }),
  uploadFile: (entity, entityId, file, kind) => {
    const fd = new FormData();
    fd.append('entity', entity);
    fd.append('entity_id', entityId);
    if (kind) fd.append('kind', kind);
    fd.append('file', file);
    return api.post('/crm/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadFile: (id) => api.get(`/crm/files/${id}/download`, { responseType: 'blob' }),
  deleteFile: (id) => api.delete(`/crm/files/${id}`),
  // Журнал
  entityAudit: (entity, entityId) => api.get('/crm/audit', { params: { entity, entity_id: entityId } }),
  auditFeed: (params) => api.get('/crm/audit/feed', { params }),
  // Корзина
  listTrash: (entity) => api.get('/crm/trash', { params: entity ? { entity } : {} }),
  restoreTrash: (entity, id) => api.post(`/crm/trash/${entity}/${id}/restore`),
  purgeTrash: (entity, id, confirm) => api.delete(`/crm/trash/${entity}/${id}`, { params: { confirm } }),
  // Колокольчик
  notifications: (params) => api.get('/crm/notifications', { params }),
  readNotifications: (ids) => api.post('/crm/notifications/read', { ids: ids || null }, { silentSuccess: true }),
  // Настройки
  settingsPipelines: (includeArchived) => api.get('/crm/settings/pipelines', { params: includeArchived ? { include_archived: true } : {} }),
  createPipeline: (data) => api.post('/crm/settings/pipelines', data),
  updatePipeline: (id, data) => api.patch(`/crm/settings/pipelines/${id}`, data),
  archivePipeline: (id) => api.post(`/crm/settings/pipelines/${id}/archive`),
  restorePipeline: (id) => api.post(`/crm/settings/pipelines/${id}/restore`),
  reorderPipelines: (ids) => api.post('/crm/settings/pipelines/reorder', { ids }),
  createStage: (pipelineId, data) => api.post(`/crm/settings/pipelines/${pipelineId}/stages`, data),
  updateStage: (id, data) => api.patch(`/crm/settings/stages/${id}`, data),
  deleteStage: (id, moveTo) => api.delete(`/crm/settings/stages/${id}`, { params: moveTo ? { move_to: moveTo } : {} }),
  reorderStages: (pipelineId, ids) => api.post(`/crm/settings/pipelines/${pipelineId}/stages/reorder`, { ids }),
  createLeadStatus: (data) => api.post('/crm/lead-statuses', data),
  updateLeadStatus: (id, data) => api.patch(`/crm/lead-statuses/${id}`, data),
  deleteLeadStatus: (id, moveTo) => api.delete(`/crm/lead-statuses/${id}`, { params: moveTo ? { move_to: moveTo } : {} }),
  reorderLeadStatuses: (ids) => api.post('/crm/lead-statuses/reorder', { ids }),
  settingsSources: () => api.get('/crm/settings/sources'),
  createSource: (data) => api.post('/crm/settings/sources', data),
  updateSource: (id, data) => api.patch(`/crm/settings/sources/${id}`, data),
  reorderSources: (ids) => api.post('/crm/settings/sources/reorder', { ids }),
  checklistItems: () => api.get('/crm/checklist-items'),
  createChecklistItem: (data) => api.post('/crm/checklist-items', data),
  updateChecklistItem: (id, data) => api.patch(`/crm/checklist-items/${id}`, data),
  deleteChecklistItem: (id) => api.delete(`/crm/checklist-items/${id}`),
  reorderChecklistItems: (ids) => api.post('/crm/checklist-items/reorder', { ids }),
  // Отчёты
  reportSources: (params) => api.get('/crm/reports/sources', { params }),
  reportManagers: (params) => api.get('/crm/reports/managers', { params }),
  reportStageDuration: (params) => api.get('/crm/reports/stage-duration', { params }),
  reportLostReasons: (params) => api.get('/crm/reports/lost-reasons', { params }),
  reportForecast: (params) => api.get('/crm/reports/forecast', { params }),
  reportTasks: (params) => api.get('/crm/reports/tasks', { params }),
  reportMeta: () => api.get('/crm/reports/meta'),
  reportQuery: (data) => api.post('/crm/reports/query', data),
  reportExport: (data) => api.post('/crm/reports/export.xlsx', data, { responseType: 'blob' }),
  exportTable: (title, columns, rows) => api.post('/crm/reports/export-table.xlsx', { title, columns, rows }, { responseType: 'blob' }),
  savedReports: () => api.get('/crm/reports/saved'),
  createSavedReport: (data) => api.post('/crm/reports/saved', data),
  updateSavedReport: (id, data) => api.patch(`/crm/reports/saved/${id}`, data),
  deleteSavedReport: (id) => api.delete(`/crm/reports/saved/${id}`),
  // Приём с сайта (Фаза 2)
  getIntakeToken: () => api.get('/crm/intake/token'),
  regenIntakeToken: () => api.post('/crm/intake/token'),
  // WON → WMS (Фаза 5)
  provisionDeal: (id, data) => api.post(`/crm/deals/${id}/provision`, data || {}),
  // AI (Фаза 4)
  aiQualifyLead: (id) => api.post(`/crm/leads/${id}/ai-qualify`),
  getAiConfig: () => api.get('/crm/ai-config'),
  updateAiConfig: (data) => api.patch('/crm/ai-config', data),
  setAiKey: (apiKey) => api.post('/crm/ai-key', { api_key: apiKey }),
  setAiAnthropicKey: (apiKey) => api.post('/crm/ai-anthropic-key', { api_key: apiKey }),
  setAiProxy: (proxy) => api.post('/crm/ai-proxy', { proxy }),
  // Отчёты (Фаза 6)
  funnelReport: (pipelineId, params) => api.get('/crm/reports/funnel', { params: { ...(pipelineId ? { pipeline_id: pipelineId } : {}), ...(params || {}) } }),
  // Кастомные поля (Фаза 6b)
  listCustomFields: (entity) => api.get('/crm/custom-fields', { params: entity ? { entity } : {} }),
  createCustomField: (data) => api.post('/crm/custom-fields', data),
  deleteCustomField: (id) => api.delete(`/crm/custom-fields/${id}`),
  getCustomValues: (entity, entityId) => api.get(`/crm/custom-values/${entity}/${entityId}`),
  setCustomValues: (entity, entityId, values) => api.put(`/crm/custom-values/${entity}/${entityId}`, values),
};

// Облако: бренд склада, свой домен, реквизиты исполнителя (api/platform_api.py)
export const platformApi = {
  self: () => api.get('/platform/self'),
  saveBrand: (data) => api.put('/platform/self/brand', data),
  uploadBrandFile: (kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/platform/self/brand/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteBrandFile: (kind) => api.delete(`/platform/self/brand/${kind}`),
  domains: () => api.get('/platform/self/domains'),
  addDomain: (host) => api.post('/platform/self/domains', { host }),
  verifyDomain: (host) => api.post(`/platform/self/domains/${encodeURIComponent(host)}/verify`, null, { silentSuccess: true }),
  deleteDomain: (host) => api.delete(`/platform/self/domains/${encodeURIComponent(host)}`),
  requisites: () => api.get('/platform/requisites'),
  saveRequisites: (data) => api.put('/platform/requisites', data),
  // Облако Н3: тариф склада (модули для меню и маршрутов, лимиты)
  plan: () => api.get('/platform/plan'),
  // 28.09 «Каталог модулей»: список, включённые целиком, заявка «Хочу этот модуль»
  modules: () => api.get('/platform/modules'),
  saveModules: (enabled) => api.put('/platform/modules', { enabled }, { silentSuccess: true }),
  requestModule: (key) => api.post(`/platform/modules/${encodeURIComponent(key)}/request`, null, { silentSuccess: true }),
  // Кабинет платформы — только владелец Full-Fix
  adminOperators: (stats = true) => api.get('/platform/admin/operators', { params: { stats } }),
  adminCatalog: () => api.get('/platform/admin/catalog'),
  adminSetPlan: (slug, data) => api.put(`/platform/admin/operators/${encodeURIComponent(slug)}/plan`, data),
  adminSetStatus: (slug, status) => api.post(`/platform/admin/operators/${encodeURIComponent(slug)}/status`, { status }),
  adminSetTrial: (slug, data) => api.post(`/platform/admin/operators/${encodeURIComponent(slug)}/trial`, data),
  adminBrand: (slug) => api.get(`/platform/admin/operators/${encodeURIComponent(slug)}/brand`),
  // Заявки на склад (поток B): одобрение создаёт склад в облаке, в ответе — ссылка (показать один раз)
  adminSignups: (status) => api.get('/platform/admin/signups', { params: status ? { status } : {} }),
  adminApproveSignup: (id, plan) => api.post(`/platform/admin/signups/${id}/approve`, { plan }, { silentSuccess: true }),
  adminRejectSignup: (id, reason, spam = false) => api.post(`/platform/admin/signups/${id}/reject`, { reason, spam }),
  adminRelinkSignup: (id) => api.post(`/platform/admin/signups/${id}/relink`, null, { silentSuccess: true }),
  // Подписка (28.09): «Тариф и оплата» склада и счета в кабинете владельца
  subscription: () => api.get('/platform/subscription'),
  exportAll: () => api.get('/platform/self/export-all', { responseType: 'blob', timeout: 300000 }),
  createInvoice: (data) => api.post('/platform/subscription/invoice', data, { silentSuccess: true }),
  checkInvoice: (id) => api.post(`/platform/subscription/invoices/${id}/check`, null, { silentSuccess: true }),
  invoiceDoc: (id, doc) => api.get(`/platform/subscription/invoices/${id}/${doc}.pdf`, { responseType: 'blob' }),
  adminInvoices: (slug) => api.get(`/platform/admin/operators/${encodeURIComponent(slug)}/invoices`),
  adminInvoicePaid: (id) => api.post(`/platform/admin/invoices/${id}/paid`),
  adminInvoiceCancel: (id) => api.post(`/platform/admin/invoices/${id}/cancel`),
  adminInvoiceDoc: (id, doc) => api.get(`/platform/admin/invoices/${id}/${doc}.pdf`, { responseType: 'blob' }),
  adminPaidUntil: (slug, data) => api.post(`/platform/admin/operators/${encodeURIComponent(slug)}/paid-until`, data),
  adminVendor: () => api.get('/platform/admin/vendor'),
  adminSaveVendor: (data) => api.put('/platform/admin/vendor', data),
  adminBillingTick: () => api.post('/platform/admin/billing-tick'),
};

// Облако Н3: заявка на склад (без входа) и установка пароля по одноразовой ссылке
// (api/signup_api.py). Страницы сами показывают итог — silentSuccess.
export const signupApi = {
  config: () => api.get('/public/signup/config'),
  checkSlug: (slug) => api.get('/public/signup/slug-check', { params: { slug } }),
  submit: (data) => api.post('/public/signup', data, { silentSuccess: true }),
  checkLink: (token) => api.post('/public/setup-password/check', { token }, { silentSuccess: true }),
  setPassword: (token, password) => api.post('/public/setup-password', { token, password }, { silentSuccess: true }),
};

// Облако Н3: мастер первого запуска склада (api/setup_wizard.py; у full-fix enabled=false)
export const setupApi = {
  status: () => api.get('/setup/status'),
  setStep: (key, state) => api.post(`/setup/steps/${key}`, { state }, { silentSuccess: true }),
  finish: () => api.post('/setup/finish', null, { silentSuccess: true }),
  reopen: () => api.post('/setup/reopen', null, { silentSuccess: true }),
  // «Пропустить настройку и открыть систему» — выключить режим фокуса
  skip: () => api.post('/setup/skip', null, { silentSuccess: true }),
  // Пробная этикетка агенту печати и её статус
  testPrint: (data) => api.post('/setup/test-print', data, { silentSuccess: true }),
  testPrintStatus: (jobId) => api.get(`/setup/test-print/${jobId}`),
  // Профиль бизнеса (интервью мастера) и совет по разделам
  profile: () => api.get('/setup/profile'),
  saveProfile: (data) => api.put('/setup/profile', data, { silentSuccess: true }),
  // Каталог модулей (другой поток): 404 — ещё не выложен, мастер не падает
  modulesList: () => api.get('/platform/modules'),
  modulesSave: (enabled) => api.put('/platform/modules', { enabled }, { silentSuccess: true }),
  // Привязка Telegram (третий поток): 404 — «скоро»
  telegramLink: () => api.get('/notifications/telegram/link'),
  telegramStatus: () => api.get('/notifications/telegram/status'),
  // Цены шага мастера: базовый прайс + свои услуги, шаблон и загрузка Excel
  prices: () => api.get('/setup/prices'),
  savePrices: (services) => api.put('/setup/prices', { services }, { silentSuccess: true }),
  pricesTemplate: () => api.get('/setup/prices/template.xlsx', { responseType: 'blob' }),
  importPrices: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/setup/prices/import', fd, { silentSuccess: true });
  },
};

export default api;

// Тележки и палеты подбора (задача №14): создать в подборе, положить собранное, поставить в буферную ячейку по скану
export const pickCartsApi = {
  list: (status = 'OPEN') => api.get('/picking/carts', { params: { status } }),
  get: (id) => api.get(`/picking/carts/${id}`),
  create: (kind, name) => api.post('/picking/carts', { kind, name: name || null }, { silentSuccess: true }),
  place: (cartCode, cellCode) => api.post('/picking/carts/place', { cart_code: cartCode, cell_code: cellCode }, { silentSuccess: true }),
  take: (id) => api.post(`/picking/carts/${id}/take`, null, { silentSuccess: true }),
  close: (id) => api.post(`/picking/carts/${id}/close`, null, { silentSuccess: true }),
  reopen: (id) => api.post(`/picking/carts/${id}/reopen`, null, { silentSuccess: true }),
  print: (id, data) => api.post(`/picking/carts/${id}/print`, data, { silentSuccess: true }),
};

// Передача отгрузки (задачи №16–18): подтверждение коробов сканом, хранение до отъезда, фото погрузки
export const shipmentHandoverApi = {
  get: (id) => api.get(`/processing/shipments/${id}/handover`),
  locations: (id) => api.get(`/processing/shipments/${id}/locations`),
  confirm: (id, code, undo) => api.post(`/processing/shipments/${id}/handover/confirm`, { code, undo: !!undo }, { silentSuccess: true }),
  store: (id, cell_code, codes) => api.post(`/processing/shipments/${id}/handover/store`, { cell_code, codes: codes || null }, { silentSuccess: true }),
  unstore: (id, codes) => api.post(`/processing/shipments/${id}/handover/unstore`, { codes: codes || null }, { silentSuccess: true }),
  photos: (id) => api.get(`/processing/shipments/${id}/photos`),
  uploadPhotos: (id, files, note) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return api.post(`/processing/shipments/${id}/photos`, fd, { params: note ? { note } : {}, headers: { 'Content-Type': 'multipart/form-data' }, silentSuccess: true, timeout: 120000 });
  },
  deletePhoto: (id, photoId) => api.delete(`/processing/shipments/${id}/photos/${photoId}`, { silentSuccess: true }),
};

// Маркетинг (раздел WMS): обзор по каналам, расходы, заявки и скорость, подключение Метрики
export const marketingApi = {
  channels: () => api.get('/marketing/channels'),
  overview: (params) => api.get('/marketing/overview', { params }),
  timeline: (params) => api.get('/marketing/timeline', { params }),
  costs: (params) => api.get('/marketing/costs', { params }),
  addCost: (data) => api.post('/marketing/costs', data, { silentSuccess: true }),
  deleteCost: (id) => api.delete(`/marketing/costs/${id}`, { silentSuccess: true }),
  leads: (params) => api.get('/marketing/leads', { params }),
  setLeadChannel: (id, channel) => api.patch(`/marketing/leads/${id}/channel`, { channel: channel || null }, { silentSuccess: true }),
  seo: (days) => api.get('/marketing/seo', { params: { days }, timeout: 90000 }),
  settings: () => api.get('/marketing/settings'),
  sync: (days) => api.post('/marketing/settings/sync', { days }, { silentSuccess: true, timeout: 120000 }),
};

// Маркетинг: Telegram-мониторинг («кому нужен фулфилмент»)
export const marketingTgApi = {
  signals: (params) => api.get('/marketing/tg/signals', { params }),
  ingest: (data) => api.post('/marketing/tg/signals/ingest', data, { silentSuccess: true, timeout: 90000 }),
  setStatus: (id, status) => api.post(`/marketing/tg/signals/${id}/status`, { status }, { silentSuccess: true }),
  toLead: (id) => api.post(`/marketing/tg/signals/${id}/lead`, null, { silentSuccess: true }),
  ai: (id) => api.post(`/marketing/tg/signals/${id}/ai`, null, { silentSuccess: true, timeout: 90000 }),
  sources: () => api.get('/marketing/tg/sources'),
  addSource: (data) => api.post('/marketing/tg/sources', data, { silentSuccess: true }),
  editSource: (id, data) => api.patch(`/marketing/tg/sources/${id}`, data, { silentSuccess: true }),
  deleteSource: (id) => api.delete(`/marketing/tg/sources/${id}`, { silentSuccess: true }),
  rules: () => api.get('/marketing/tg/rules'),
  addRule: (data) => api.post('/marketing/tg/rules', data, { silentSuccess: true }),
  editRule: (id, data) => api.patch(`/marketing/tg/rules/${id}`, data, { silentSuccess: true }),
  deleteRule: (id) => api.delete(`/marketing/tg/rules/${id}`, { silentSuccess: true }),
  testRule: (text) => api.post('/marketing/tg/rules/test', { text }, { silentSuccess: true }),
  accounts: () => api.get('/marketing/tg/accounts'),
  saveApiKeys: (api_id, api_hash) => api.post('/marketing/tg/accounts/config', { api_id, api_hash }, { silentSuccess: true }),
  authStart: (phone, proxy) => api.post('/marketing/tg/accounts/auth/start', { phone, proxy: proxy || null }, { silentSuccess: true, timeout: 60000 }),
  authConfirm: (phone, code, password) => api.post('/marketing/tg/accounts/auth/confirm', { phone, code, password: password || null }, { silentSuccess: true, timeout: 60000 }),
  toggleAccount: (id) => api.post(`/marketing/tg/accounts/${id}/toggle`, null, { silentSuccess: true }),
  deleteAccount: (id) => api.delete(`/marketing/tg/accounts/${id}`, { silentSuccess: true }),
  readNow: () => api.post('/marketing/tg/read-now', null, { silentSuccess: true, timeout: 600000 }),
};
